import Ionicons from "@expo/vector-icons/Ionicons";
import * as ImagePicker from "expo-image-picker";
import {
  useLocalSearchParams,
  useRouter,
  type ErrorBoundaryProps,
} from "expo-router";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  AppState,
  FlatList,
  Image,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { ApiError, chatApi, revealApi } from "@/api";
import { createChatSocket } from "@/api/chat-socket";
import { USE_MOCK } from "@/api/config";
import {
  CATCH_UP_MAX_PAGES,
  CATCH_UP_PAGE,
  lastServerMessageId,
  mergeMessages,
} from "@/features/realtime/message-merge";
import { ErrorFallback } from "@/components/ui/error-fallback";
import { EmojiPicker } from "@/components/emoji-picker";
import { PlusDeckUpsell } from "@/components/plus-deck-upsell";
import { ReportModal } from "@/components/report-modal";
import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { Radius, Spacing } from "@/constants/theme";
import { useAuthedImage } from "@/hooks/use-authed-image";
import { useScheme, useTheme, type Scheme } from "@/hooks/use-theme";
import { canLoadOlder, moreBehind } from "@/lib/load-older";
import { createLatch } from "@/lib/single-tap";
import { entriesForThread, visiblePending } from "@/lib/outbox-core";
import { onOutboxSent, useOutboxStore } from "@/state/outbox-store";
import { probe, probeErrorName } from "@/lib/timing-probe";
import { useThreadFollow } from "@/hooks/use-thread-follow";
import { useKeyboardProbe } from "@/hooks/use-keyboard-probe";
import { formatMessageTime } from "@/lib/message-time";
import { systemMessageText } from "@/lib/system-message";
import { socialStrings as strings } from "@/i18n/features/social.i18n";
import {
  CONVERSATION_PROMPT_CATEGORIES,
  conversationDeckClient,
  type ConversationPromptCategory,
  type ConversationPromptSuggestion,
} from "@/lib/conversation-deck";
import {
  cachedMessages,
  forgetChatMessages,
  rememberMessages,
} from "@/lib/chat-message-cache";
import { confirm, notify } from "@/lib/dialog";
import { firstEmoji } from "@/lib/emoji";
import { availablePhotoWidth, photoBox } from "@/lib/photo-layout";
import { chatRevealUiState } from "@/lib/chat-reveal-state";
import { isPlusUser } from "@/lib/plan";
import { useAuthStore } from "@/state/auth-store";
import { requestReflection } from "@/state/reflection-store";
import { useSessionStore } from "@/state/session-store";
import { isPlusDeckCategory } from "@/types";
import { handleFor } from "@/lib/profile-handle";
import type {
  ChatMessage,
  EndConversationOutcome,
  PhotoExchangeState,
  RevealResult,
  UserAvatarConfig,
} from "@/types";

const REVEAL_FIELDS = [
  "age",
  "about",
  "interests",
  "gender",
  "values",
  "politics",
];
const MESSAGE_REACTIONS = ["👍", "😂", "🔥", "❤️", "👀"];
const TYPING_REFRESH_MS = 1400;
const TYPING_STOP_MS = 2200;
const PEER_TYPING_TTL_MS = 3500;

/**
 * A slow backstop behind live delivery, not the delivery mechanism.
 *
 * This used to be a 1.8s full re-read of the entire conversation — which worked,
 * but re-downloaded every message anyone had ever sent, forty times a minute,
 * for as long as the screen was open. Now that messages arrive over the socket
 * and gaps are closed with a cursor, this only has to cover the case where the
 * socket is quietly wedged: connected as far as the client knows, delivering
 * nothing.
 */
const CATCH_UP_INTERVAL_MS = 20_000;

// Keep the conversation in a centred column (matches the inbox width) instead
// of stretching edge-to-edge on wide/web viewports. A no-op on phones.
const CHAT_MAX_WIDTH = 760;

/**
 * The dismiss button on the request banner, which is positioned absolutely and
 * therefore sits OUTSIDE the layout everything else in that banner shares.
 *
 * Named rather than repeated because three places have to agree on it: the
 * button itself, the spacer that keeps copy clear of it, and the rail that
 * holds Withdraw. They were three separate literals, and the rail's did not
 * match — so the Withdraw pill was drawn over the X and the banner could not
 * be closed.
 */
const BANNER_CLOSE_INSET = 8;
const BANNER_CLOSE_SIZE = 28;
/** Everything the close occupies, measured in from the banner's right edge. */
const BANNER_CLOSE_FOOTPRINT = BANNER_CLOSE_INSET + BANNER_CLOSE_SIZE;

type DeckDraftInsert = {
  promptText: string;
};

type ConversationDeckColors = {
  backdrop: string;
  sheetBackground: string;
  sheetBorder: string;
  sheetShadow: string;
  handle: string;
  title: string;
  subtitle: string;
  closeBackground: string;
  closeIcon: string;
  categoryIdleBackground: string;
  categoryIdleBorder: string;
  categoryIdleText: string;
  categoryIdleIcon: string;
  categorySelectedBackground: string;
  categorySelectedBorder: string;
  categorySelectedText: string;
  categorySelectedIcon: string;
  categorySelectedShadow: string;
  promptBackground: string;
  promptBorder: string;
  promptBadge: string;
  promptText: string;
  promptHint: string;
  divider: string;
  followUpIconBackground: string;
  followUpIcon: string;
  followUpLabel: string;
  followUpText: string;
  draftBackground: string;
  draftBorder: string;
  draftTitle: string;
  draftBody: string;
  primaryBackground: string;
  primaryBorder: string;
  primaryText: string;
  primaryIcon: string;
  secondaryBackground: string;
  secondaryBorder: string;
  secondaryText: string;
  secondaryIcon: string;
  ghostBorder: string;
  ghostText: string;
  phraseText: string;
  phraseIcon: string;
};

function getConversationDeckColors(
  c: ReturnType<typeof useTheme>,
  scheme: Scheme,
): ConversationDeckColors {
  if (scheme === "light") {
    return {
      backdrop: "rgba(15, 23, 42, 0.36)",
      sheetBackground: c.background,
      sheetBorder: c.border,
      sheetShadow: "#7187A6",
      handle: "#A7B2C1",
      title: c.text,
      subtitle: c.textSecondary,
      closeBackground: c.backgroundElement,
      closeIcon: c.textMuted,
      categoryIdleBackground: c.surface,
      categoryIdleBorder: c.border,
      categoryIdleText: c.textSecondary,
      categoryIdleIcon: c.textMuted,
      categorySelectedBackground: c.tint,
      categorySelectedBorder: c.tint,
      categorySelectedText: c.onTint,
      categorySelectedIcon: c.onTint,
      categorySelectedShadow: c.tint,
      promptBackground: c.surface,
      promptBorder: c.border,
      promptBadge: c.tint,
      promptText: c.text,
      promptHint: c.textSecondary,
      divider: c.border,
      followUpIconBackground: c.backgroundSelected,
      followUpIcon: c.tint,
      followUpLabel: c.tint,
      followUpText: c.text,
      draftBackground: c.surfaceAlt,
      draftBorder: c.border,
      draftTitle: c.text,
      draftBody: c.textSecondary,
      primaryBackground: c.tint,
      primaryBorder: c.tint,
      primaryText: c.onTint,
      primaryIcon: c.onTint,
      secondaryBackground: c.surface,
      secondaryBorder: c.tint,
      secondaryText: c.tint,
      secondaryIcon: c.tint,
      ghostBorder: c.border,
      ghostText: c.textSecondary,
      phraseText: c.textMuted,
      phraseIcon: c.textMuted,
    };
  }

  return {
    backdrop: "rgba(15, 23, 42, 0.42)",
    sheetBackground: c.background,
    sheetBorder: "rgba(112, 147, 184, 0.22)",
    sheetShadow: "#000000",
    handle: "#90A3BA",
    title: "#E7EEF8",
    subtitle: "#8F9DB0",
    closeBackground: "rgba(43, 59, 80, 0.72)",
    closeIcon: "#A7B7CA",
    categoryIdleBackground: "rgba(6, 17, 29, 0.38)",
    categoryIdleBorder: "rgba(116, 144, 176, 0.24)",
    categoryIdleText: "#95A2B5",
    categoryIdleIcon: "#8D9AAE",
    categorySelectedBackground: "#3B95FF",
    categorySelectedBorder: "#74B9FF",
    categorySelectedText: "#EAF2FF",
    categorySelectedIcon: "#EAF2FF",
    categorySelectedShadow: "#3B95FF",
    promptBackground: "rgba(11, 30, 47, 0.72)",
    promptBorder: "rgba(95, 128, 163, 0.25)",
    promptBadge: "#7DAFFF",
    promptText: "#EAF1FA",
    promptHint: "#8D9CAF",
    divider: "rgba(119, 148, 178, 0.22)",
    followUpIconBackground: "rgba(84, 113, 143, 0.36)",
    followUpIcon: "#4E9DFF",
    followUpLabel: "#81B3FF",
    followUpText: "#DCE7F4",
    draftBackground: "rgba(45, 69, 94, 0.5)",
    draftBorder: "rgba(135, 170, 210, 0.2)",
    draftTitle: "#E7EEF8",
    draftBody: "#A8B6C8",
    primaryBackground: "#4E9DFF",
    primaryBorder: "#4E9DFF",
    primaryText: "#06111D",
    primaryIcon: "#06111D",
    secondaryBackground: "rgba(6, 17, 29, 0.22)",
    secondaryBorder: "#4E9DFF",
    secondaryText: "#4E9DFF",
    secondaryIcon: "#4E9DFF",
    ghostBorder: "rgba(132, 154, 180, 0.35)",
    ghostText: "#A7B7CA",
    phraseText: "#8394AB",
    phraseIcon: "#8394AB",
  };
}

const DECK_CATEGORY_LABEL_KEYS: Record<
  ConversationPromptCategory,
  (typeof strings)[keyof typeof strings]
> = {
  start: strings.deckCategoryStart,
  keep_going: strings.deckCategoryKeepGoing,
  fun: strings.deckCategoryFun,
  get_to_know: strings.deckCategoryGetToKnow,
  go_deeper: strings.deckCategoryGoDeeper,
  make_a_plan: strings.deckCategoryMakeAPlan,
  wrap_up: strings.deckCategoryWrapUp,
};

const DECK_CATEGORY_ICON_NAMES: Record<
  ConversationPromptCategory,
  ComponentProps<typeof Ionicons>["name"]
> = {
  start: "power-outline",
  keep_going: "trending-up-outline",
  fun: "happy-outline",
  get_to_know: "person-add-outline",
  go_deeper: "trail-sign-outline",
  make_a_plan: "calendar-outline",
  wrap_up: "checkmark-circle-outline",
};

const DECK_PROMPT_BADGE_KEYS: Record<
  ConversationPromptCategory,
  (typeof strings)[keyof typeof strings]
> = {
  start: strings.deckPromptBadgeStart,
  keep_going: strings.deckPromptBadgeKeepGoing,
  fun: strings.deckPromptBadgeFun,
  get_to_know: strings.deckPromptBadgeGetToKnow,
  go_deeper: strings.deckPromptBadgeGoDeeper,
  make_a_plan: strings.deckPromptBadgeMakeAPlan,
  wrap_up: strings.deckPromptBadgeWrapUp,
};

function replyAuthorLabel(
  message: ChatMessage | NonNullable<ChatMessage["replyTo"]>,
  currentUserId: string | undefined,
  t: TFunction,
) {
  return message.senderId === "me" || message.senderId === currentUserId
    ? t(strings.chatYou)
    : t(strings.chatThem);
}

/**
 * The URI scheme the picker handed back, as a number a probe can carry.
 *
 * `uploadImage` sends `{ uri, name, type }` straight to React Native's
 * `FormData`, and what the platform can do with that depends entirely on the
 * scheme: a `file:` path is read directly, a `content:` URI has to go through
 * the ContentResolver, and anything else is not a local file at all. Which one
 * expo-image-picker returns varies by Android version and by whether the pick
 * came from the photo picker or a document provider — so on a failure this is
 * the first thing worth knowing, and it is a category rather than a path, so no
 * filename reaches a log.
 */
function uriScheme(uri: string | undefined): number {
  if (!uri) return -1;
  if (uri.startsWith("file:")) return 0;
  if (uri.startsWith("content:")) return 1;
  if (uri.startsWith("data:")) return 2;
  if (uri.startsWith("http:") || uri.startsWith("https:")) return 3;
  if (uri.startsWith("blob:")) return 4;
  return 5;
}

/**
 * Keeps a render failure on this screen from taking the whole app down.
 *
 * The composer holds text the person has typed and not yet sent. A render
 * failure that unmounts the screen loses it silently, which is the same class
 * of loss R-03 fixed on the network side.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  return <ErrorFallback error={error} retry={retry} scope="chat" />;
}

export default function ChatRoom() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const c = useTheme();
  const { t } = useTranslation();
  const scheme = useScheme();
  const lightMode = scheme === "light";
  const currentUserId = useAuthStore((s) => s.user?.id);
  const accessToken = useAuthStore((s) => s.session?.accessToken);
  const chats = useSessionStore((s) => s.chats);
  const chat = chats.find((ch) => ch.id === id);
  const ensureChat = useSessionStore((s) => s.ensureChat);

  // The inbox pages, so arriving here from a notification, a deep link, or a
  // conversation below the first page can leave the list without this row —
  // and everything on this screen is derived from it. Rendered without it the
  // header said "Someone" over a stranger's avatar, the share-more state
  // disappeared, and Report had no one to report. Fetch it by id instead of
  // assuming the list already knows.
  useEffect(() => {
    if (!id || chat) return;
    void ensureChat(id);
  }, [id, chat, ensureChat]);
  // "Cannot type here" — true for an ended conversation AND a dormant one.
  const closedToWriting = chat?.active === false;
  // Dormant specifically: still here, still readable, reopenable by agreement.
  const dormant = chat?.state === "inactive";
  const setActiveChat = useSessionStore((s) => s.setActiveChat);
  const markChatRead = useSessionStore((s) => s.markChatRead);
  const receiveChatMessage = useSessionStore((s) => s.receiveChatMessage);
  const endChat = useSessionStore((s) => s.endChat);
  const clearChat = useSessionStore((s) => s.clearChat);
  const requestReopen = useSessionStore((s) => s.requestReopen);
  const unblockChat = useSessionStore((s) => s.unblock);
  const resume = useSessionStore((s) => s.resume);
  const respondToReopen = useSessionStore((s) => s.respondToReopen);
  const refreshChats = useSessionStore((s) => s.refreshChats);
  const receiveChatReveal = useSessionStore((s) => s.receiveChatReveal);

  // Seeded from the cache, so reopening a conversation paints its last page on
  // the FIRST render rather than after a round trip. A new chat starts empty
  // and pays one fetch, as before.
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    cachedMessages(id),
  );
  /**
   * What the list renders: the same messages, newest first, because the list
   * is `inverted`. The STATE stays oldest-first — every merge, send and
   * catch-up path appends the way the API pages read — and only the render
   * order flips. Bounded by what is held, which is bounded by paging.
   */
  /**
   * Messages still waiting to reach the server, held by the OUTBOX rather than
   * by this screen — which is what lets them survive navigating away, and what
   * stops a failed send from destroying them.
   */
  const enqueueOutbox = useOutboxStore((s) => s.enqueue);
  const pending = useOutboxStore((s) => s.entries);
  const pendingHere = useMemo(
    () => (id ? entriesForThread(pending, "chat", id) : []),
    [pending, id],
  );

  /**
   * Pending messages render as ordinary bubbles at the live edge, carrying a
   * clock instead of a timestamp. They sort after every sent message because
   * they were written after them.
   */
  /**
   * Keys of messages already in the thread, so a pending bubble disappears the
   * moment its saved twin arrives — by ANY route.
   *
   * A queued message can come back three ways: the send response, this room's
   * socket broadcast (which includes the sender — `chat.gateway.ts` emits with
   * `server.to(room)`, not `socket.broadcast`), and a catch-up page after a
   * reconnect. The saved row's id is the server's, the pending row's is
   * `outbox-…`, so without a shared key the list holds both for a frame — one
   * row too many — exactly as the old ordering held one too few. Matching on
   * the echoed `clientMessageId` closes both, in either order, with no timing
   * assumption at all.
   */
  const withPending = useMemo<ChatMessage[]>(
    () => [
      ...messages,
      ...visiblePending(pendingHere, messages).map((entry) => ({
        id: entry.localId,
        chatId: entry.threadId,
        senderId: "me",
        content: entry.content,
        timestamp: entry.createdAt,
        replyTo: null,
        pending: true as const,
        pendingSlow: entry.slow,
        clientMessageId: entry.clientMessageId,
      })),
    ],
    [messages, pendingHere],
  );

  const listData = useMemo(() => [...withPending].reverse(), [withPending]);

  // MEASUREMENT for the send shake. `drawn` is what the list actually renders;
  // `saved` is what the server has given us. Through a healthy send the drawn
  // count must never change — the pending row is replaced by its saved twin,
  // not removed and re-added — so a wobble here IS the bug, stated directly
  // rather than inferred from content-size events that keyboard animations
  // also fire.
  useEffect(() => {
    probe("thread.rows", {
      saved: messages.length,
      drawn: withPending.length,
    });
  }, [messages.length, withPending.length]);

  // Measurement only, for the intermittent keyboard overlap reported on build
  // 13 — see `use-keyboard-probe.ts`. Nothing here changes layout.
  const composerRef = useRef<View | null>(null);
  useKeyboardProbe(composerRef, "chat");
  /**
   * Whether the first read has happened yet — which is not the same question as
   * "are there messages", and conflating the two is what put "You're connected.
   * Break the ice." in front of every reader of every conversation while their
   * thread was still in flight.
   *
   * A cached page counts as ready: it is real history, already on screen.
   *
   * A conversation with NOTHING cached stays pending and waits for the read,
   * and that includes one the cache has seen and knows to be empty — the cache
   * refuses to store an empty page so that this cannot be shortcut. Rendering
   * the empty state from cache would put "Break the ice." on screen for a round
   * trip in the case it is most often wrong: an empty chat is reopened because
   * the peer finally wrote. Waiting costs a blank moment; not waiting costs a
   * false statement about the conversation.
   */
  const [firstRead, setFirstRead] = useState<"pending" | "ready" | "failed">(
    () => (cachedMessages(id).length > 0 ? "ready" : "pending"),
  );
  // Whether the thread continues past the oldest message currently held, and
  // whether we are in the middle of finding out.
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState("");
  const [endMenuOpen, setEndMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  // Effective plan, so referral-granted Plus never sees a lock it has paid
  // for in kind. Only decides what is DRAWN — the server decides what is sent.
  const hasPlus = isPlusUser(useAuthStore((s) => s.user));
  const [plusUpsellOpen, setPlusUpsellOpen] = useState(false);
  const [deckCategory, setDeckCategory] =
    useState<ConversationPromptCategory>("start");
  const [deckSuggestion, setDeckSuggestion] =
    useState<ConversationPromptSuggestion | null>(null);
  // A draw in flight. Without it the sheet showed the category badge over
  // nothing while the first prompt loaded — or, on a category switch, the
  // PREVIOUS category's prompt under the new badge.
  const [deckDrawing, setDeckDrawing] = useState(false);
  const [deckDraftInsert, setDeckDraftInsert] =
    useState<DeckDraftInsert | null>(null);
  const [ending, setEnding] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [reveal, setReveal] = useState<RevealResult | null>(null);
  const [revealPending, setRevealPending] = useState(false);
  const [reactingMessageId, setReactingMessageId] = useState<string | null>(
    null,
  );
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [peerTyping, setPeerTyping] = useState(false);
  const [photo, setPhoto] = useState<PhotoExchangeState | null>(null);
  const [photoUnreachable, setPhotoUnreachable] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoViewerAssetId, setPhotoViewerAssetId] = useState<string | null>(
    null,
  );
  const [reportPhotoAssetId, setReportPhotoAssetId] = useState<string | null>(
    null,
  );
  const [dismissedRequestSheetKey, setDismissedRequestSheetKey] = useState<
    string | null
  >(null);
  const listRef = useRef<FlatList<ChatMessage>>(null);
  const {
    onScroll: onThreadScroll,
    onContentSizeChange,
    follow: followThread,
    maintainVisibleContentPosition,
  } = useThreadFollow(listRef);
  const composerInputRef = useRef<TextInput>(null);
  const chatSocketRef = useRef<ReturnType<typeof createChatSocket> | null>(
    null,
  );
  const typingActiveRef = useRef(false);
  const lastTypingSentAtRef = useRef(0);
  const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peerTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deckRequestRef = useRef(0);

  const clearTypingStopTimer = useCallback(() => {
    if (typingStopTimerRef.current) {
      clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
  }, []);

  const clearPeerTypingTimer = useCallback(() => {
    if (peerTypingTimerRef.current) {
      clearTimeout(peerTypingTimerRef.current);
      peerTypingTimerRef.current = null;
    }
  }, []);

  const sendTypingState = useCallback((isTyping: boolean) => {
    chatSocketRef.current?.sendTyping(isTyping);
    typingActiveRef.current = isTyping;
    lastTypingSentAtRef.current = Date.now();
  }, []);

  const stopLocalTyping = useCallback(() => {
    clearTypingStopTimer();
    if (typingActiveRef.current) {
      sendTypingState(false);
    }
  }, [clearTypingStopTimer, sendTypingState]);

  const scheduleTypingStop = useCallback(() => {
    clearTypingStopTimer();
    typingStopTimerRef.current = setTimeout(() => {
      stopLocalTyping();
    }, TYPING_STOP_MS);
  }, [clearTypingStopTimer, stopLocalTyping]);

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next);

      if (closedToWriting) return;

      if (!next.trim()) {
        stopLocalTyping();
        return;
      }

      const now = Date.now();
      if (
        !typingActiveRef.current ||
        now - lastTypingSentAtRef.current > TYPING_REFRESH_MS
      ) {
        sendTypingState(true);
      }

      scheduleTypingStop();
    },
    [closedToWriting, scheduleTypingStop, sendTypingState, stopLocalTyping],
  );

  const pickDeckPrompt = useCallback(
    async (category: ConversationPromptCategory) => {
      if (!currentUserId) return null;
      deckRequestRef.current += 1;
      const requestId = deckRequestRef.current;
      // Cleared only by the request that is still the latest — an older
      // response racing home must not stop the spinner a newer draw owns.
      setDeckDrawing(true);
      try {
        const next = await conversationDeckClient.next(
          currentUserId,
          category,
        );
        if (requestId === deckRequestRef.current) {
          setDeckSuggestion(next);
          setDeckDrawing(false);
        }
        return next;
      } catch {
        if (requestId === deckRequestRef.current) {
          setDeckSuggestion(null);
          setDeckDrawing(false);
        }
        return null;
      }
    },
    [currentUserId],
  );

  const openDeck = useCallback(() => {
    setDeckDraftInsert(null);
    setDeckOpen(true);
    void pickDeckPrompt(deckCategory);
  }, [deckCategory, pickDeckPrompt, setDeckDraftInsert, setDeckOpen]);

  const closeDeck = useCallback(() => {
    setDeckOpen(false);
    setDeckDraftInsert(null);
  }, [setDeckDraftInsert, setDeckOpen]);

  const selectDeckCategory = useCallback(
    (category: ConversationPromptCategory) => {
      setDeckCategory(category);
      setDeckDraftInsert(null);
      void pickDeckPrompt(category);
    },
    [pickDeckPrompt, setDeckCategory, setDeckDraftInsert],
  );

  const showAnotherDeckPrompt = useCallback(() => {
    setDeckDraftInsert(null);
    void pickDeckPrompt(deckCategory);
  }, [deckCategory, pickDeckPrompt, setDeckDraftInsert]);

  const focusComposerSoon = useCallback(() => {
    setTimeout(() => composerInputRef.current?.focus(), 80);
  }, []);

  const insertDeckText = useCallback(
    (promptText: string, mode: "replace" | "append") => {
      const trimmedPrompt = promptText.trim();
      if (!trimmedPrompt) return;
      const nextText =
        mode === "append" && text.trim()
          ? `${text.trimEnd()}\n\n${trimmedPrompt}`
          : trimmedPrompt;
      handleTextChange(nextText);
      closeDeck();
      focusComposerSoon();
    },
    [
      closeDeck,
      focusComposerSoon,
      handleTextChange,
      text,
    ],
  );

  const addDeckTextToMessage = useCallback(
    (promptText: string) => {
      if (text.trim()) {
        setDeckDraftInsert({ promptText });
        return;
      }
      insertDeckText(promptText, "replace");
    },
    [insertDeckText, setDeckDraftInsert, text],
  );

  const addDeckPromptToMessage = useCallback(() => {
    if (!deckSuggestion) return;
    addDeckTextToMessage(deckSuggestion.text);
  }, [addDeckTextToMessage, deckSuggestion]);

  const insertDeckDraft = useCallback(
    (mode: "replace" | "append") => {
      if (!deckDraftInsert) return;
      insertDeckText(deckDraftInsert.promptText, mode);
    },
    [deckDraftInsert, insertDeckText],
  );

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const next = await chatApi.messages(id, { limit: CATCH_UP_PAGE });
      setMessages(next);
      // A page that came back full is the only hint that history continues past
      // it. Exactly-a-page-remaining costs one empty read when the user asks for
      // more, which is cheaper than a count on every open.
      setHasOlder(moreBehind(next.length, CATCH_UP_PAGE));
      setFirstRead("ready");
    } catch {
      // The chat may have just ended or been navigated away from — a stale
      // background fetch shouldn't surface as an unhandled rejection.
      //
      // But it must not be silent on the FIRST read either. Left pending, the
      // thread would sit blank forever with nothing to say why and no way to
      // retry; and the old behaviour — claiming the conversation was empty —
      // is worse still. A read that already succeeded stays `ready`: this is
      // one failed poll over history that is still on screen.
      setFirstRead((current) => (current === "pending" ? "failed" : current));
    }
    // `setFirstRead` is listed because the React Compiler infers it and refuses
    // to memoize a callback whose stated deps it cannot match. It is a state
    // setter, so it is stable and changes nothing at runtime.
  }, [id, setFirstRead]);

  /**
   * Synchronous, unlike `loadingOlder`.
   *
   * `loadingOlder` is React state and the scroll handler runs at 60fps, so the
   * value it reads does not change until React commits — one flick therefore
   * issued one request per frame, every one of them carrying the SAME `before`
   * cursor. `mergeMessages` hid that on this thread by deduping on id; the
   * practice transcript prepends its page directly and duplicated turns.
   *
   * The same latch, against the same mistake, as the auth submits in
   * `lib/single-tap.ts`. The state flag stays, but only to draw the header.
   */
  const olderLatch = useRef(createLatch()).current;

  /**
   * Walk further back through the thread.
   *
   * Opening a conversation reads the newest page, not all of it — a conversation
   * is never deleted, so there is no length it cannot reach. This is how the rest
   * is reached, a page at a time, from the oldest message currently held.
   */
  const loadOlder = useCallback(async () => {
    if (!id) return;
    const oldest = messages.find((message) => !message.id.startsWith("local-"));
    if (!oldest) return;

    if (!olderLatch.claim()) return;

    setLoadingOlder(true);
    try {
      const older = await chatApi.messages(id, {
        before: oldest.id,
        limit: CATCH_UP_PAGE,
      });
      setMessages((prev) => mergeMessages(prev, older));
      setHasOlder(moreBehind(older.length, CATCH_UP_PAGE));
    } catch {
      // Leave the control in place so it can simply be tried again.
    } finally {
      setLoadingOlder(false);
      olderLatch.release();
    }
  }, [id, messages, olderLatch]);

  /**
   * Reaching back, decided by the list reporting its older end rather than by
   * an offset this screen would have to interpret. The thread is inverted, so
   * the older end is the scroll END and `onEndReached` is the platform's own
   * word for "the reader is reaching for history". The guards are
   * `lib/load-older.ts`, shared by all three threads; each one is load-bearing
   * — `onEndReached` can fire repeatedly while the reader sits at the start of
   * the thread, and without `hasOlder` every one of those would be a request
   * for the page before the first message.
   */
  const maybeLoadOlder = useCallback(() => {
    const anchored = messages.some(
      (message) => !message.id.startsWith("local-"),
    );
    if (
      !canLoadOlder({
        loading: olderLatch.held,
        hasOlder,
        hasAnchor: anchored,
      })
    ) {
      return;
    }
    void loadOlder();
  }, [hasOlder, loadOlder, messages, olderLatch]);

  /**
   * Fetch whatever arrived while we were not listening.
   *
   * The socket is the delivery path; this closes the gaps it cannot see — the
   * reconnect after a phone slept in a pocket, the resume from background, the
   * connection that died without saying so. It reads forward from the last
   * message we actually hold, so the cost is the size of the gap rather than the
   * size of the conversation.
   *
   * With nothing held yet there is no cursor to read from, so it falls back to a
   * full read; that is the same first load the screen does on mount.
   */
  const catchUp = useCallback(async () => {
    if (!id) return;
    try {
      let cursor = lastServerMessageId(messages);

      if (!cursor) {
        const all = await chatApi.messages(id);
        setMessages((prev) => mergeMessages(prev, all));
        return;
      }

      for (let page = 0; page < CATCH_UP_MAX_PAGES; page += 1) {
        const batch = await chatApi.messages(id, {
          after: cursor,
          limit: CATCH_UP_PAGE,
        });
        if (batch.length === 0) return;

        setMessages((prev) => mergeMessages(prev, batch));

        // A short page means the gap is closed. A full one means there may be
        // more behind it, so walk on from the last id we just took.
        if (batch.length < CATCH_UP_PAGE) return;
        const next = batch[batch.length - 1]?.id;
        if (!next || next === cursor) return;
        cursor = next;
      }
    } catch {
      // Same reasoning as `load`: the conversation may have ended or the screen
      // gone away mid-flight. The next connect or resume tries again.
    }
  }, [id, messages]);

  // Held in refs so the socket, interval, and resume effects can reload the
  // thread without taking either callback as a dependency — which would tear the
  // connection down and rebuild it every time their identity changed.
  //
  // `catchUp` makes this load-bearing rather than tidy: it closes over `messages`
  // for its cursor, so its identity changes with every message that arrives.
  // Reached through the ref, callers always get the freshest cursor and nothing
  // re-subscribes.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const catchUpRef = useRef(catchUp);
  useEffect(() => {
    catchUpRef.current = catchUp;
  }, [catchUp]);

  /*
    ONE writer to the cache, and it mirrors what is on screen.

    Writing it from `load()` alone would have cached only what the fetch
    returned, so a message that arrived over the socket, a reaction, or a reply
    would be missing from the page painted on reopen — the cache would be
    reliably a little bit wrong, which is worse than being absent. Here it
    tracks every path that changes the thread, whatever changed it.

    Optimistic sends are excluded: a `local-` message is not history yet, and
    one whose send ultimately failed must not be resurrected by a later open.
  */
  useEffect(() => {
    if (!id) return;
    rememberMessages(
      id,
      messages.filter((message) => !message.id.startsWith("outbox-")),
    );
  }, [id, messages]);

  /**
   * A queued message reached the server: merge the saved row and move the
   * inbox row with the SERVER's timestamp.
   *
   * The outbox removes its own entry before announcing, so the pending bubble
   * and the saved one are never both on screen. `mergeMessages` dedupes on id,
   * which covers the socket echo of this same message arriving in either
   * order. `saved.id` is what `markMessageSeen` dedupes by, so the echo's own
   * inbox patch becomes a no-op rather than a second one.
   */
  useEffect(() => {
    if (!id) return;
    return onOutboxSent((threadKind, threadId, saved) => {
      if (threadKind !== "chat" || threadId !== id) return;
      const message = saved as ChatMessage;
      setMessages((prev) => mergeMessages(prev, [message]));
      receiveChatMessage(id, message.id, {
        preview: message.content.slice(0, 140),
        sentAt: message.timestamp,
      });
    });
  }, [id, receiveChatMessage]);

  // The first read happens whatever state the conversation is in. A closed or
  // dormant one is still readable, and for a dormant one that is the entire
  // promise on the card — "your conversation is still here" is not credible if
  // opening it shows an empty thread. Only the POLLING is conditional.
  useEffect(() => {
    if (!id) return;
    // Deferred a tick, as the polling effect below always has been: `load`
    // resolves into `setMessages`, and the lint rule reads a bare call in an
    // effect body as a synchronous setState.
    const timer = setTimeout(() => {
      void load();
    }, 0);
    return () => clearTimeout(timer);
  }, [id, load]);

  // Photo exchange state, read once per conversation. It only changes when
  // someone in this chat acts, and every one of those actions returns the new
  // state — so there is nothing here worth polling for.
  //
  // A failure here is remembered rather than swallowed: without it the row sits
  // disabled under a hint that implies the exchange is simply not on offer,
  // which is indistinguishable from the API being unreachable.
  const loadPhotoState = useCallback(async () => {
    if (!id) return;
    try {
      setPhoto(await chatApi.photoExchange(id));
      setPhotoUnreachable(false);
    } catch {
      setPhotoUnreachable(true);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const timer = setTimeout(() => void loadPhotoState(), 0);
    return () => clearTimeout(timer);
  }, [id, loadPhotoState]);

  // Same reason as `loadRef`: kept out of the socket effect's dependencies.
  const photoRef = useRef(loadPhotoState);
  useEffect(() => {
    photoRef.current = loadPhotoState;
  }, [loadPhotoState]);

  useEffect(() => {
    // Nothing new can arrive once the conversation is closed, dormant, or being
    // ended, so stop asking rather than firing stale requests.
    if (!id || closedToWriting || ending) return;
    const timer = setInterval(() => {
      void catchUpRef.current();
    }, CATCH_UP_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [closedToWriting, ending, id]);

  // Coming back from the background is the common case, not the rare one: iOS
  // and Android both tear the socket down on a sleeping phone, and it comes back
  // without any notion of what it missed. In mock mode there is no socket to
  // have missed anything, but the resume read is harmless and keeps the two
  // paths behaving the same.
  useEffect(() => {
    if (!id || closedToWriting || ending) return undefined;
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") void catchUpRef.current();
    });
    return () => subscription.remove();
  }, [closedToWriting, ending, id]);

  useEffect(() => {
    if (!id || closedToWriting || USE_MOCK || !accessToken) return undefined;

    const socket = createChatSocket(id, accessToken, {
      // Live delivery. This is what the 1.8s full re-read used to stand in for.
      onMessage: (message) => {
        setMessages((prev) => mergeMessages(prev, [message]));
      },
      // Fires on the first connect and on every reconnect after it, which is
      // precisely when the thread may have moved on without us.
      onConnect: () => {
        void catchUpRef.current();
      },
      onReaction: (message) => {
        setMessages((prev) => mergeMessages(prev, [message]));
      },
      onTyping: (userId, isTyping) => {
        if (userId === currentUserId) return;

        clearPeerTypingTimer();

        if (!isTyping) {
          setPeerTyping(false);
          return;
        }

        setPeerTyping(true);
        peerTypingTimerRef.current = setTimeout(() => {
          setPeerTyping(false);
          peerTypingTimerRef.current = null;
        }, PEER_TYPING_TTL_MS);
      },
      onRevealUpdate: (status, userId) => {
        if (status === "revealed") {
          setRevealPending(false);
        } else if (userId === currentUserId) {
          setRevealPending(true);
        } else {
          setRevealPending(false);
        }
        receiveChatReveal(id, {
          status,
          actorId: userId,
          currentUserId,
        });
        // Detached, so it needs its own catch: the list refresh is a nicety
        // here, and a rejection nobody owns — an expired session most of all —
        // would surface as an uncaught error over the thread.
        void refreshChats().catch(() => {});
      },
      onPhotoExchange: () => {
        // Re-read rather than trusting the frame: the socket says only that
        // something moved, and every access check lives behind the API.
        void photoRef.current();
        void loadRef.current();
      },
    });

    chatSocketRef.current = socket;

    return () => {
      stopLocalTyping();
      clearPeerTypingTimer();
      setPeerTyping(false);
      socket.disconnect();
      if (chatSocketRef.current === socket) {
        chatSocketRef.current = null;
      }
    };
  }, [
    accessToken,
    clearPeerTypingTimer,
    currentUserId,
    id,
    closedToWriting,
    refreshChats,
    receiveChatReveal,
    stopLocalTyping,
  ]);

  // Mark this conversation as the one being read so its messages don't add to
  // the unread badge or trigger notifications while it's open. `setActiveChat`
  // marks it read itself — which now also advances the server-side read mark,
  // so the badge stays cleared across a restart, not just for this process.
  useEffect(() => {
    if (!id) return undefined;
    setActiveChat(id);
    return () => {
      setActiveChat(null);
    };
  }, [id, setActiveChat]);

  // Following the thread down is `useThreadFollow`, driven by content size
  // rather than by this array. Keying on the newest id was already careful
  // about history loading — the group thread keyed on `length` and was not —
  // but it still animated the FIRST paint, and still ran before the rows had
  // laid out. Both ids stay: the read receipt below is keyed on them.
  const newestMessage = messages[messages.length - 1];
  const newestMessageId = newestMessage?.id;

  // A peer message that lands while the thread is on screen is read the
  // moment it renders, so the server's mark has to say so — otherwise the
  // next inbox refresh resurrects a badge for messages the person just
  // watched arrive. Keyed on the newest id so each message posts once, and
  // only for the peer's: my own sends have nothing to mark.
  const newestIsPeers =
    newestMessage != null &&
    newestMessage.senderId !== "me" &&
    newestMessage.senderId !== currentUserId;
  useEffect(() => {
    if (!id || !newestIsPeers) return;
    markChatRead(id);
  }, [id, newestIsPeers, newestMessageId, markChatRead]);

  useEffect(() => {
    if (
      !id ||
      (!chat?.revealed && chat?.revealStatus !== "revealed") ||
      reveal ||
      closedToWriting
    ) {
      return undefined;
    }

    let active = true;

    async function loadRevealDetails() {
      try {
        const res = await revealApi.request(id, REVEAL_FIELDS);
        if (!active) return;
        if (res.status === "revealed" && res.result) {
          setReveal(res.result);
        }
      } catch {
        // Ignore — the chat may have ended before the reveal resolved.
      } finally {
        if (active) setRevealPending(false);
      }
    }

    void loadRevealDetails();

    return () => {
      active = false;
    };
  }, [chat?.revealed, chat?.revealStatus, id, reveal, closedToWriting]);

  /**
   * Hand the message to the outbox, which owns it from here.
   *
   * This function used to append an optimistic bubble, await the send, and
   * remove the bubble in a bare `catch` — which meant that offline, the
   * message vanished and `setText("")` had already thrown the typed text away.
   * Filmed four times on build 12. Now the send cannot fail from the sender's
   * point of view: the bubble is queued, stays on screen, and is delivered
   * when the connection comes back.
   *
   * The text is cleared only once the message is ACCEPTED, so a refusal by a
   * full queue leaves the composer exactly as it was.
   */
  function send(content: string) {
    const body = content.trim();
    if (!body || !id || closedToWriting) return;

    const replyTargetId =
      replyingTo && !replyingTo.id.startsWith("outbox-")
        ? replyingTo.id
        : undefined;

    const result = enqueueOutbox({
      threadKind: "chat",
      threadId: id,
      content: body,
      replyTargetId,
    });

    if (!result.accepted) {
      // Visible, honest, and recoverable — the opposite of the silent drop
      // this replaced. Nothing already accepted is destroyed to make room.
      notify(t(strings.messageQueueFullTitle), t(strings.messageQueueFullBody));
      return;
    }

    setText("");
    setReplyingTo(null);
    stopLocalTyping();
    // Writing into a thread IS asking to see it, so this outranks "the reader
    // scrolled up, leave them alone". Without it a sender who had scrolled up
    // watched their own message land off-screen.
    followThread();
  }

  async function requestReveal() {
    if (!id || closedToWriting) return;
    setRevealing(true);
    try {
      const res = await revealApi.request(id, REVEAL_FIELDS);
      if (res.status === "revealed" && res.result) {
        setReveal(res.result);
        setRevealPending(false);
        receiveChatReveal(id, {
          status: "revealed",
          actorId: currentUserId,
          currentUserId,
        });
      } else {
        setRevealPending(true);
        receiveChatReveal(id, {
          status: "pending_peer",
          actorId: currentUserId,
          currentUserId,
        });
      }
      // `void` detaches it from the try below, so it carries its own catch.
      void refreshChats().catch(() => {});
    } catch {
      // Ignore — e.g. the conversation ended before the request completed.
    } finally {
      setRevealing(false);
    }
  }

  async function reactToMessage(message: ChatMessage, reaction: string) {
    if (!id) return;
    const nextReaction = message.reaction === reaction ? null : reaction;
    setReactingMessageId(null);
    setMessages((prev) =>
      prev.map((item) =>
        item.id === message.id ? { ...item, reaction: nextReaction } : item,
      ),
    );

    if (message.id.startsWith("local-")) return;

    try {
      const updated = await chatApi.react(id, message.id, nextReaction);
      setMessages((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch {
      void load();
    }
  }

  async function endConversation(outcome: EndConversationOutcome) {
    if (!id || ending) return;
    setEnding(true);
    try {
      await endChat(id, outcome);
      setEndMenuOpen(false);
      router.replace("/(tabs)/inbox");
      // Leaving is a reflection moment — but not when the user just blocked.
      if (outcome !== "blocked") requestReflection("conversation");
      notify(
        outcome === "blocked"
          ? t("social.chat.blockedTitleShort")
          : t("social.chat.endedTitle"),
        outcome === "blocked"
          ? t("social.chat.blockedBodyShort")
          : t("social.chat.endedBody"),
      );
    } finally {
      setEnding(false);
    }
  }

  /**
   * Pick a photo, then send it as the ask or as the answer.
   *
   * There is no "request, then choose" step: your own photo goes with the
   * request, so nobody is ever holding an agreement they have not yet acted on.
   * The picker asks for permission itself, and a cancelled pick is a no-op.
   */
  async function pickAndSend(mode: "request" | "accept") {
    if (!id || photoBusy) return;

    const picked = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsMultipleSelection: false,
      quality: 1,
    });
    if (picked.canceled || !picked.assets?.[0]) return;

    const asset = picked.assets[0];
    setPhotoBusy(true);
    // What we are about to send, before anything can go wrong with it. Photo
    // exchange fails on Android with the generic "something went wrong" and
    // leaves NOTHING behind: a 47k-line logcat from a failing session carried no
    // request, no error and no Sentry event, because this catch turns every
    // non-400/409 into one localized string and drops the exception. These are
    // shape facts only — no filename, no URI, no bytes — and they are what
    // separates "the picker handed us something the upload cannot encode" from
    // "the request never left" from "the server answered and refused".
    const uploadStarted = Date.now();
    probe("chat.photo.pick", {
      mode: mode === "request" ? 0 : 1,
      scheme: uriScheme(asset.uri),
      bytes: asset.fileSize ?? -1,
      width: asset.width ?? -1,
      height: asset.height ?? -1,
      hasName: asset.fileName ? 1 : 0,
      hasType: asset.mimeType ? 1 : 0,
      // Web hands over a real `File`; native must not, and if it ever did the
      // multipart part would be built down the wrong branch of `uploadImage`.
      hasFile: asset.file ? 1 : 0,
    });
    try {
      const upload = {
        uri: asset.uri,
        name: asset.fileName ?? "photo.jpg",
        type: asset.mimeType ?? "image/jpeg",
        // Web only; undefined on native, where `uri` is what gets sent.
        file: asset.file,
      };
      const next =
        mode === "request"
          ? await chatApi.requestPhotoExchange(id, upload)
          : await chatApi.acceptPhotoExchange(id, upload);
      probe("chat.photo.sent", { ms: Date.now() - uploadStarted });
      setPhoto(next);
      if (mode === "accept") void load();
    } catch (err) {
      // Three different things, told apart so the advice fits:
      //
      // 400 — the server refused this particular image. It never says which
      //       check failed, so neither do we; another photo may well work.
      // 409 — the request being answered is gone. Picking another photo cannot
      //       possibly help, so saying so would send them round a loop.
      // else — offline, 5xx, expired session. Not the photo's fault either.
      const status = err instanceof ApiError ? err.status : 0;
      // The failure, before it is turned into advice. The class name goes in the
      // probe NAME because a probe carries only numbers — the same reason
      // `use-keyboard-probe.ts` puts the screen there — and it is the field that
      // separates the causes: `TypeError` is fetch failing before a round trip,
      // `ApiError` means the server answered, `SessionExpiredError` means the
      // refresh path. `ms` distinguishes an immediate throw from a timeout.
      probe(`chat.photo.fail.${probeErrorName(err)}`, {
        ms: Date.now() - uploadStarted,
        status,
        mode: mode === "request" ? 0 : 1,
      });
      if (status === 400) {
        notify(t(strings.photoRejectedTitle), (err as ApiError).message);
      } else if (status === 409) {
        notify(t(strings.photoGoneTitle), t(strings.photoGoneBody));
        await loadPhotoState();
      } else {
        notify(t("errors.generic"), t("errors.tryAgainInAMoment"));
      }
    } finally {
      setPhotoBusy(false);
    }
  }

  /** The menu row. Routes to whichever action the current state allows. */
  async function startPhotoExchange() {
    if (!photo) return;
    if (photo.pending && !photo.pending.byMe) return pickAndSend("accept");
    if (photo.pending?.byMe) return;
    if (photo.canRequest) return pickAndSend("request");
  }

  // Neither of these can leave the error to surface on its own: both are drawn
  // from state that may have moved, and an unhandled rejection here is a red
  // box in the user's face over a card that simply needs redrawing. Re-reading
  // is the recovery — whatever the server now says is the truth.
  async function answerPhotoExchange(accept: boolean) {
    if (!id || photoBusy) return;
    if (accept) return pickAndSend("accept");

    setPhotoBusy(true);
    try {
      setPhoto(await chatApi.declinePhotoExchange(id));
    } catch {
      await loadPhotoState();
    } finally {
      setPhotoBusy(false);
    }
  }

  async function withdrawPhotoExchange() {
    if (!id || photoBusy) return;
    setPhotoBusy(true);
    try {
      setPhoto(await chatApi.cancelPhotoExchange(id));
    } catch {
      await loadPhotoState();
    } finally {
      setPhotoBusy(false);
    }
  }

  async function deleteMyPhoto(assetId: string) {
    if (!id) return;
    // The viewer closes BEFORE the question.
    //
    // This used to ask with the viewer still up, on the reasoning that
    // `ConfirmDialog` mounts its Modal at ask time and so paints above an
    // already-open one. That reasoning is real but it is about WEB: it fixes
    // react-native-web's portal ordering, where the dialog was rendering
    // behind a modal mounted later. It says nothing about iOS, where UIKit
    // refuses to present a second modal over one already presented no matter
    // when the React component mounted — the dialog never appears and its
    // window eats every touch (device-observed twice: the group member menu on
    // build 25, the practice pack sheet on build 30).
    //
    // The cost is the one the old comment named: a cancelled delete returns to
    // the thread rather than to the photo. That is a worse cancel and a
    // working screen, which beats a better cancel behind a frozen one.
    setPhotoViewerAssetId(null);
    const ok = await confirm({
      title: t(strings.photoDeleteTitle),
      message: t(strings.photoDeleteBody),
      confirmLabel: t(strings.photoDelete),
      cancelLabel: t(strings.cancel),
      destructive: true,
    });
    if (!ok) return;

    await chatApi.deletePhoto(id, assetId);
    setPhoto(await chatApi.photoExchange(id));
    void load();
  }

  /**
   * Wipe my copy of the thread and stay put. Not an ending, so no reflection
   * prompt and no navigation away — the conversation is still here, just empty.
   */
  async function clearConversation() {
    if (!id || ending) return;
    // Same defect and same fix as Block: this is reached from inside the end
    // sheet, and a confirmation asked while that Modal is up renders behind
    // its portal on web — the tap looked like it did nothing. The sheet goes
    // first, then the question.
    setEndMenuOpen(false);
    const ok = await confirm({
      title: t(strings.clearConfirmTitle),
      message: t(strings.clearConfirmBody),
      confirmLabel: t(strings.clearConfirm),
      cancelLabel: t(strings.cancel),
      destructive: true,
    });
    if (!ok) return;

    setEnding(true);
    try {
      await clearChat(id);
      setMessages([]);
      // Or reopening the conversation would repaint the page we just cleared.
      forgetChatMessages(id);
      notify(t(strings.clearedTitle), t(strings.clearedBody));
    } finally {
      setEnding(false);
    }
  }

  async function unblock() {
    if (!id || reopenBusy || !chat) return;
    // The app's own dialog, not `Alert.alert` — on web that is an empty function
    // and every confirmation through it is silently discarded. See `lib/dialog`.
    const agreed = await confirm({
      title: t(strings.unblockConfirmTitle, { name: displayName }),
      message: t(strings.unblockConfirmBody),
      confirmLabel: t(strings.unblock),
    });
    if (!agreed) return;

    setReopenBusy(true);
    try {
      await unblockChat(id);
    } catch {
      notify(t(strings.reopenFailedTitle), t(strings.reopenFailedBody));
    } finally {
      setReopenBusy(false);
    }
  }

  async function resumeChat() {
    if (!id || reopenBusy) return;
    // No confirmation dialog, unlike Unblock. Unblocking changes what another
    // person can do; this changes nothing outside this screen and tells nobody,
    // so a "are you sure" would be ceremony over an action with no consequence
    // to anyone else.
    setReopenBusy(true);
    try {
      await resume(id);
    } catch {
      notify(t(strings.reopenFailedTitle), t(strings.reopenFailedBody));
    } finally {
      setReopenBusy(false);
    }
  }

  async function askToReopen() {
    if (!id || reopenBusy) return;
    setReopenBusy(true);
    try {
      await requestReopen(id);
    } catch {
      notify(t(strings.reopenFailedTitle), t(strings.reopenFailedBody));
    } finally {
      setReopenBusy(false);
    }
  }

  async function answerReopen(accept: boolean) {
    if (!id || reopenBusy) return;
    setReopenBusy(true);
    try {
      await respondToReopen(id, accept);
    } catch {
      notify(t(strings.reopenFailedTitle), t(strings.reopenFailedBody));
    } finally {
      setReopenBusy(false);
    }
  }

  const revealUi = chatRevealUiState({
    hasRevealResult: Boolean(reveal),
    chatRevealed: chat?.revealed,
    revealStatus: chat?.revealStatus,
    revealRequestedByMe: chat?.revealRequestedByMe,
    revealRequestedByPeer: chat?.revealRequestedByPeer,
    localRevealPending: revealPending,
  });
  const revealed = revealUi.revealed;
  const revealRequestReceived = revealUi.requestReceived;
  const revealRequestPending = revealUi.requestPending;
  const revealBannerLabel =
    revealUi.banner === "received"
      ? strings.revealReceived
      : revealUi.banner === "sent"
        ? strings.revealPending
        : null;
  const revealActionLabel = revealRequestReceived
    ? strings.acceptReveal
    : revealRequestPending
      ? strings.revealPendingShort
      : strings.requestReveal;
  const displayName = reveal?.firstName ?? chat?.peerName ?? "Someone";
  const cleanComposer = Platform.OS === "web" || lightMode;
  const showInactiveWarning = chat?.inactiveWarning === true;
  const reopenAskedByPeer = Boolean(chat?.reopenRequestedByPeer);
  const reopenAskedByMe = Boolean(chat?.reopenRequestedByMe);

  // The photo row reads its state off one object, so the menu, the card, and
  // the bubbles can never disagree about where the exchange has got to.
  const photoRevealed = photo?.status === "revealed";
  const photoAskedByMe = Boolean(photo?.pending?.byMe);
  const photoAskedByPeer = Boolean(photo?.pending && !photo.pending.byMe);
  const photoActionable =
    !closedToWriting &&
    !photoRevealed &&
    (photoUnreachable || photoAskedByPeer || Boolean(photo?.canRequest));
  const photoActionLabel = photoRevealed
    ? strings.photoExchanged
    : photoAskedByPeer
      ? strings.photoRespond
      : photoAskedByMe
        ? strings.photoWaiting
        : strings.photoExchange;
  const photoActionHint = photoUnreachable
    ? "errors.tryAgainInAMoment"
    : photoRevealed
    ? strings.photoExchangedHint
    : photoAskedByPeer
      ? strings.photoRespondHint
      : photoAskedByMe
        ? strings.photoWaitingHint
        : photo?.status === "exhausted"
          ? strings.photoExhaustedHint
          : strings.photoExchangeHint;
  const photoIcon = photoRevealed
    ? "checkmark-circle-outline"
    : photoAskedByMe
      ? "time-outline"
      : "image-outline";
  const requestSheetKey = [
    revealUi.banner ?? "no-reveal",
    photoAskedByPeer ? "photo-peer" : photoAskedByMe ? "photo-me" : "no-photo",
  ].join(":");
  const showRequestSheet =
    (revealBannerLabel || photoAskedByPeer || photoAskedByMe) &&
    dismissedRequestSheetKey !== requestSheetKey;

  /**
   * Close the reaction picker when a MOUSE clicks anywhere outside it.
   *
   * `onTouchEnd` above covers touch devices, but a mouse fires no touch events,
   * so on web the picker could only be closed by clicking the message it
   * belonged to. A screen-level `onStartShouldSetResponder` looks like the
   * cross-platform answer and is not: it unmounts the picker before the native
   * emoji input can focus, which is why chat-reaction-ui.test.ts forbids one.
   *
   * `click` rather than `mousedown`, deliberately — a document listener runs
   * after the target's own handler, so pressing an emoji still reacts and only
   * then dismisses. Attached a tick late so the very click that OPENED the
   * picker cannot immediately close it as it finishes bubbling.
   */
  useEffect(() => {
    if (Platform.OS !== "web") return;
    if (reactingMessageId === null) return;
    if (typeof document === "undefined") return;

    const close = () => setReactingMessageId(null);
    const timer = setTimeout(() => {
      document.addEventListener("click", close);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", close);
    };
  }, [reactingMessageId]);

  return (
    <SafeAreaView
      style={[styles.safe, { backgroundColor: c.background }]}
      edges={["top"]}
      onTouchEnd={() => {
        if (reactingMessageId !== null) setReactingMessageId(null);
      }}
    >
      <View
        style={[
          styles.header,
          // On web, render the header as a full-width top navbar (matching the
          // inbox top bar) with the peer details, reveal action and menu on it.
          Platform.OS === "web" && styles.headerWeb,
          Platform.OS === "web" && { backgroundColor: c.surface },
          { borderBottomColor: c.border },
        ]}
      >
        <Pressable
          onPress={() => router.replace("/(tabs)/inbox")}
          hitSlop={10}
          style={styles.back}
        >
          <Ionicons name="chevron-back" size={26} color={c.text} />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.chatViewProfileA11y, {
            name: displayName,
          })}
          onPress={() =>
            chat &&
            router.push(`/guest-profile?v=${handleFor(chat.peerId)}`)
          }
          // There is no profile behind a hidden peer — blocked, or the account
          // is gone — and the server refuses the request. Leaving the tap live
          // would spend a round trip to arrive at an error screen, and the
          // error itself would be the tell: it would confirm to a blocked
          // person that they had been blocked.
          disabled={!chat || chat.peerHidden === true}
          style={({ pressed }) => [
            styles.headerPerson,
            pressed && styles.headerPersonPressed,
          ]}
        >
          <View style={styles.headerAvatar}>
            <Avatar
              seed={chat?.peerAvatarSeed ?? "peer"}
              avatarConfig={chat?.peerAvatarConfig}
              name={displayName}
              size={38}
              // Same rule as the inbox row: this corner reports whether the
              // conversation is live, so the pin/vibe badge stays off it.
              showBadge={false}
            />

            {/* A dormant thread has no live presence to report. */}
            {dormant ? null : (
              <View
                style={[
                  styles.headerOnlineDot,
                  {
                    // Blue, matching the inbox row's indicator: this dot and
                    // that one report the same fact about the same person, and
                    // a peer who is a blue dot in the list and a green one in
                    // the conversation reads as two different states.
                    //
                    // `tint` and not the inbox's `primary`, because this screen
                    // is on the OTHER palette — `constants/theme.ts`, where the
                    // brand blue is `tint` (#007BFF) rather than `primary`
                    // (#2468D8). They are not the same hex. That split predates
                    // this change and runs through the whole app; taking each
                    // screen's own blue is what keeps this dot consistent with
                    // everything else around it, and hard-coding the other
                    // palette's value here would go stale exactly the way the
                    // comment this replaces did.
                    //
                    // Was `c.success`, under a comment claiming it matched the
                    // inbox's green — true when written, stale the moment the
                    // inbox moved, and invisible because the two live in
                    // different files and neither is a shared component.
                    backgroundColor: c.tint,
                    // The ring hides the avatar's edge behind the dot, so it
                    // has to be whatever the header itself is painted.
                    borderColor:
                      Platform.OS === "web" ? c.surface : c.background,
                  },
                ]}
              />
            )}
          </View>
          <View style={styles.headerInfo}>
            <View style={styles.headerNameRow}>
              <ThemedText variant="bodyStrong" numberOfLines={1}>
                {displayName}
              </ThemedText>
            </View>
            <ThemedText variant="caption" tone="muted" numberOfLines={1}>
              {revealed
                ? t(strings.revealedStatus)
                : t(strings.publicProfile)}
            </ThemedText>
          </View>
        </Pressable>
        <View style={styles.headerActions}>
          {!revealed && Platform.OS === "web" ? (
            <Pressable
              onPress={requestReveal}
              disabled={revealing || revealRequestPending}
              style={[
                styles.revealBtn,
                { backgroundColor: c.backgroundSelected },
              ]}
            >
              <Ionicons
                name={
                  revealRequestPending
                    ? "time-outline"
                    : revealRequestReceived
                      ? "checkmark-circle-outline"
                      : "arrow-redo-outline"
                }
                size={16}
                color={c.tint}
              />
              <ThemedText
                variant="caption"
                tone="tint"
                style={styles.revealText}
              >
                {t(revealActionLabel)}
              </ThemedText>
            </Pressable>
          ) : null}

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(strings.conversationOptionsA11y)}
            onPress={() => setEndMenuOpen(true)}
            hitSlop={8}
            style={[
              styles.optionsBtn,
              { backgroundColor: c.backgroundSelected },
            ]}
          >
            <Ionicons name="ellipsis-horizontal" size={18} color={c.tint} />
          </Pressable>
        </View>
      </View>

      {showRequestSheet ? (
        <View
          style={[
            styles.requestSheet,
            { backgroundColor: c.backgroundSelected, borderColor: c.border },
          ]}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(strings.close)}
            onPress={() => setDismissedRequestSheetKey(requestSheetKey)}
            hitSlop={8}
            style={styles.requestSheetClose}
          >
            <Ionicons name="close" size={16} color={c.textMuted} />
          </Pressable>

          {revealBannerLabel ? (
            <View style={styles.requestSheetRow}>
              <View style={styles.requestSheetMain}>
                <Ionicons name="hourglass-outline" size={17} color={c.tint} />
                <View style={styles.requestSheetCopy}>
                  <ThemedText
                    variant="caption"
                    tone="tint"
                    style={styles.requestSheetTitle}
                  >
                    {t(revealBannerLabel)}
                  </ThemedText>
                </View>
              </View>
              <View style={styles.requestSheetActionRail} />
            </View>
          ) : null}

          {revealBannerLabel && (photoAskedByPeer || photoAskedByMe) ? (
            <View style={[styles.requestSheetDivider, { backgroundColor: c.border }]} />
          ) : null}

          {photoAskedByPeer || photoAskedByMe ? (
            // Copy on one row, actions on the next. They used to share a row
            // via a fixed 132px rail, which two pills cannot fit inside — so
            // `flexWrap` stacked them vertically and squeezed the copy at the
            // same time. Giving the actions their own full-width row lets them
            // sit horizontally and hands the text back the width it was losing.
            <View style={styles.requestSheetStack}>
              <View style={styles.requestSheetRow}>
                <View style={styles.requestSheetMain}>
                  <Ionicons
                    name={photoAskedByPeer ? "image-outline" : "time-outline"}
                    size={17}
                    color={c.tint}
                  />
                  <View style={styles.requestSheetCopy}>
                    <ThemedText
                      variant="caption"
                      style={styles.requestSheetTitle}
                      numberOfLines={1}
                    >
                      {t(
                        photoAskedByPeer
                          ? strings.photoRequestTitle
                          : strings.photoWaiting,
                        { name: displayName },
                      )}
                    </ThemedText>
                    <ThemedText
                      variant="caption"
                      tone="muted"
                      style={styles.requestSheetHint}
                      numberOfLines={1}
                    >
                      {t(
                        photoAskedByPeer
                          ? strings.photoRequestBody
                          : strings.photoWaitingHint,
                      )}
                    </ThemedText>
                  </View>
                </View>
                {/*
                  Withdraw is a single button and fits the rail beside the copy,
                  where it has always been. Only the two-button answer needs a
                  row of its own. It has to stop short of the close, which is
                  absolutely positioned and so does not push anything aside.
                */}
                {photoAskedByMe ? (
                  <View
                    style={[
                      styles.requestSheetActionRail,
                      styles.requestSheetRailClearsClose,
                    ]}
                  >
                    <Pressable
                      accessibilityRole="button"
                      disabled={photoBusy}
                      onPress={() => void withdrawPhotoExchange()}
                      style={[
                        styles.requestSheetButton,
                        { backgroundColor: c.surfaceAlt, borderColor: c.border },
                        photoBusy && styles.sendBtnDisabled,
                      ]}
                    >
                      <ThemedText variant="caption" tone="muted">
                        {t(strings.photoWithdraw)}
                      </ThemedText>
                    </Pressable>
                  </View>
                ) : (
                  // Keeps the copy clear of the absolutely-positioned close.
                  <View style={styles.requestSheetCloseSpacer} />
                )}
              </View>

              {photoAskedByPeer ? (
                <View style={styles.requestSheetActions}>
                  <Pressable
                    accessibilityRole="button"
                    disabled={photoBusy}
                    onPress={() => void answerPhotoExchange(true)}
                    style={[
                      styles.requestSheetButton,
                      styles.requestSheetPrimaryButton,
                      { backgroundColor: c.tint, borderColor: c.tint },
                      photoBusy && styles.sendBtnDisabled,
                    ]}
                  >
                    <ThemedText
                      variant="caption"
                      style={[styles.reopenPrimaryText, { color: c.onTint }]}
                    >
                      {t(strings.photoShareBack)}
                    </ThemedText>
                  </Pressable>

                  <Pressable
                    accessibilityRole="button"
                    disabled={photoBusy}
                    onPress={() => void answerPhotoExchange(false)}
                    style={[
                      styles.requestSheetButton,
                      { backgroundColor: c.surfaceAlt, borderColor: c.border },
                      photoBusy && styles.sendBtnDisabled,
                    ]}
                  >
                    <ThemedText variant="caption" tone="muted">
                      {t(strings.photoNotNow)}
                    </ThemedText>
                  </Pressable>
                </View>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}

      <KeyboardAvoidingContainer
        style={styles.flex}
        keyboardVerticalOffset={8}
      >
        <FlatList
          ref={listRef}
          // Inverted: the newest message is item zero and renders at the
          // visual bottom, so the FIRST painted frame of the thread is its
          // latest message — there is no scroll command a reader could catch
          // the screen before. This replaced landing on the newest message by
          // scrolling to it after layout, which always painted the top of the
          // page first; the device capture showed that top for several frames
          // before the jump.
          inverted
          data={listData}
          keyExtractor={(m) => m.id}
          contentContainerStyle={styles.messages}
          // Hidden on every conversation thread, as the inbox list already
          // does. The indicator's LENGTH is not ours to set — the platform
          // draws it as the ratio of viewport to content — so the choice is
          // whether it appears at all.
          showsVerticalScrollIndicator={false}
          onScrollBeginDrag={() => {
            // The reaction sheet closes when hands go on the list, as it
            // always did.
            setReactingMessageId(null);
          }}
          onScroll={onThreadScroll}
          scrollEventThrottle={16}
          onContentSizeChange={onContentSizeChange}
          // A new message inserts at item zero — the scroll START — which
          // shifts a reader who is anywhere else by the height of that row.
          // Native anchors them through this; the web build has no
          // implementation of it (zero references in react-native-web 0.21.2)
          // and relies on browser scroll anchoring, which was measured holding
          // the reader's row to the pixel. Loading older history APPENDS in an
          // inverted list and shifts nobody.
          //
          // The hook's value, not a literal: a FOLLOWER is never anchored,
          // because anchoring them is what painted their own send a row too
          // low before the JS correction landed — see `use-thread-follow.ts`.
          maintainVisibleContentPosition={maintainVisibleContentPosition}
          // The older end of an inverted thread is the scroll end.
          onEndReached={maybeLoadOlder}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            // The FOOTER of an inverted list renders at the visual top, which
            // is the older end — where the page being fetched will appear.
            loadingOlder ? (
              <View style={styles.loadEarlier}>
                <ThemedText variant="caption" tone="muted">
                  {t(strings.loadingEarlier)}
                </ThemedText>
              </View>
            ) : null
          }
          renderItem={({ item }) => (
            <Bubble
              message={item}
              currentUserId={currentUserId}
              reactionPickerOpen={reactingMessageId === item.id}
              onToggleReactionPicker={() =>
                setReactingMessageId((current) =>
                  current === item.id ? null : item.id,
                )
              }
              onReact={(reaction) => reactToMessage(item, reaction)}
              onReply={() => setReplyingTo(item)}
              onOpenPhoto={setPhotoViewerAssetId}
            />
          )}
          ListEmptyComponent={
            /*
              Only once the first read has actually landed. While it is in
              flight the list stays blank: the header, the peer and the composer
              are already drawn, so the screen does not read as broken, and
              nothing claims the conversation is empty before anyone knows.
              A spinner was the other option and is worse — on a warm connection
              it would appear and vanish inside a couple of frames, which is the
              same flash this change exists to remove.
            */
            firstRead === "pending" ? null : firstRead === "failed" ? (
              // Same shape as the photo row's retry: one line that says what
              // went wrong and is itself the way to try again.
              <Pressable
                accessibilityRole="button"
                style={styles.intro}
                onPress={() => {
                  setFirstRead("pending");
                  void load();
                }}
              >
                <ThemedText variant="body" tone="muted" center>
                  {t(strings.messagesUnavailable)}
                </ThemedText>
              </Pressable>
            ) : (
              <View style={styles.intro}>
                <ThemedText variant="body" tone="muted" center>
                  {t(strings.connected)}
                </ThemedText>
              </View>
            )
          }
        />

      <TypingIndicator
        visible={peerTyping && !closedToWriting}
          avatarSeed={chat?.peerAvatarSeed ?? "peer"}
          avatarConfig={chat?.peerAvatarConfig}
          name={displayName}
        colors={c}
      />

      {/*
        The 24-hour notice. Purely informational and never blocking — the
        composer stays right where it is, because the way out of this state is
        simply to reply to each other.
      */}
      {showInactiveWarning ? (
        <View
          style={[
            styles.lifecycleBanner,
            { backgroundColor: c.backgroundSelected, borderColor: c.border },
          ]}
        >
          <Ionicons name="time-outline" size={17} color={c.textMuted} />
          <ThemedText
            variant="caption"
            tone="muted"
            style={styles.lifecycleBannerText}
          >
            {t(strings.chatInactiveWarning)}
          </ThemedText>
        </View>
      ) : null}

      {dormant ? (
          <View
            style={[
              styles.reopenCard,
              { backgroundColor: c.surface, borderTopColor: c.border },
            ]}
          >
            {reopenAskedByPeer ? (
              <>
                <View style={styles.reopenCopy}>
                  <ThemedText variant="bodyStrong">
                    {t(strings.reopenRequestTitle, { name: displayName })}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.reopenRequestBody)}
                  </ThemedText>
                </View>

                <View style={styles.reopenActions}>
                  <Pressable
                    accessibilityRole="button"
                    disabled={reopenBusy}
                    onPress={() => void answerReopen(true)}
                    style={[
                      styles.reopenButton,
                      styles.reopenPrimaryButton,
                      { backgroundColor: c.tint, borderColor: c.tint },
                      reopenBusy && styles.sendBtnDisabled,
                    ]}
                  >
                    <ThemedText
                      variant="caption"
                      style={[styles.reopenPrimaryText, { color: c.onTint }]}
                    >
                      {t(strings.reopenAccept)}
                    </ThemedText>
                  </Pressable>

                  <Pressable
                    accessibilityRole="button"
                    disabled={reopenBusy}
                    onPress={() => void answerReopen(false)}
                    style={[
                      styles.reopenButton,
                      { backgroundColor: c.surfaceAlt, borderColor: c.border },
                      reopenBusy && styles.sendBtnDisabled,
                    ]}
                  >
                    <ThemedText variant="caption" tone="muted">
                      {t(strings.reopenDecline)}
                    </ThemedText>
                  </Pressable>
                </View>
              </>
            ) : chat?.canResume ? (
              /*
                The third route back, in the same slot as the other two, and the
                only one that involves nobody else. This person walked away; the
                other side never knew, so coming back cannot ask them to agree
                to undo something they were never told about.
              */
              <>
                <View style={styles.reopenCopy}>
                  <ThemedText variant="bodyStrong">
                    {t(strings.leftTitle)}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.leftBody)}
                  </ThemedText>
                </View>

                <Pressable
                  accessibilityRole="button"
                  disabled={reopenBusy}
                  onPress={() => void resumeChat()}
                  style={[
                    styles.reopenButton,
                    styles.reopenPrimaryButton,
                    styles.reopenSoloButton,
                    { backgroundColor: c.tint, borderColor: c.tint },
                    reopenBusy && styles.sendBtnDisabled,
                  ]}
                >
                  <ThemedText
                    variant="caption"
                    style={[styles.reopenPrimaryText, { color: c.onTint }]}
                  >
                    {t(strings.resume)}
                  </ThemedText>
                </Pressable>
              </>
            ) : chat?.canUnblock ? (
              /*
                Same card, same place, different route back. A blocked thread and
                a dormant one both sit in Inactive and read the same — history
                intact, nothing writable — and the only thing that differs is the
                one action offered here. Shown solely to the person who placed
                the block; the other side falls through to the ordinary dormant
                copy below and is told nothing about a decision made about them.
              */
              <>
                <View style={styles.reopenCopy}>
                  <ThemedText variant="bodyStrong">
                    {t(strings.blockedTitle)}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.blockedBody)}
                  </ThemedText>
                </View>

                <Pressable
                  accessibilityRole="button"
                  disabled={reopenBusy}
                  onPress={() => void unblock()}
                  style={[
                    styles.reopenButton,
                    styles.reopenPrimaryButton,
                    styles.reopenSoloButton,
                    { backgroundColor: c.tint, borderColor: c.tint },
                    reopenBusy && styles.sendBtnDisabled,
                  ]}
                >
                  <ThemedText
                    variant="caption"
                    style={[styles.reopenPrimaryText, { color: c.onTint }]}
                  >
                    {t(strings.unblock)}
                  </ThemedText>
                </Pressable>
              </>
            ) : (
              <>
                <View style={styles.reopenCopy}>
                  <ThemedText variant="bodyStrong">
                    {t(strings.inactiveTitle)}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.inactiveBody)}
                  </ThemedText>
                </View>

                {/*
                  One button, no text field. A reopen request deliberately
                  carries no message: letting it would turn asking for consent
                  into a way of messaging someone who has already gone quiet.
                */}
                {reopenAskedByMe ? (
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.reopenPending)}
                  </ThemedText>
                ) : chat?.canRequestReopen ? (
                  <Pressable
                    accessibilityRole="button"
                    disabled={reopenBusy}
                    onPress={() => void askToReopen()}
                    style={[
                      styles.reopenButton,
                      styles.reopenPrimaryButton,
                      styles.reopenSoloButton,
                      { backgroundColor: c.tint, borderColor: c.tint },
                      reopenBusy && styles.sendBtnDisabled,
                    ]}
                  >
                    <ThemedText
                      variant="caption"
                      style={[styles.reopenPrimaryText, { color: c.onTint }]}
                    >
                      {t(strings.reopenAsk)}
                    </ThemedText>
                  </Pressable>
                ) : chat?.reopenCooldown ? (
                  <ThemedText variant="caption" tone="muted">
                    {t(strings.reopenCooldown)}
                  </ThemedText>
                ) : chat?.peerGone ? (
                  /*
                    The one case worth explaining without a button. Their
                    account is gone, so there is no route back and never will
                    be — and left unsaid this reads as a broken card rather than
                    a conversation that is still theirs to keep.
                  */
                  <ThemedText variant="caption" tone="muted" center>
                    {t(strings.peerGone)}
                  </ThemedText>
                ) : null}
                {/*
                  Nothing at all in the remaining case, which is the other
                  person having exited quietly. Any explanation here would tell
                  them a decision was made about them — so the card just reads
                  as an ordinary dormant thread with no action.
                */}
              </>
            )}
          </View>
        ) : closedToWriting ? (
          <View
            style={[
              styles.inactiveBar,
              { borderTopColor: c.border, backgroundColor: c.surface },
            ]}
          >
            <Ionicons
              name="information-circle-outline"
              size={18}
              color={c.textMuted}
            />
            <ThemedText variant="caption" tone="muted" center>
              {/*
                Same closed bar, different reason. "This conversation has
                closed" would read as the other person having ended it, when in
                fact they stepped away from the whole app and the thread is
                waiting for them.
              */}
              {t(
                chat?.peerUnavailable
                  ? strings.peerUnavailable
                  : strings.closed,
              )}
            </ThemedText>
          </View>
        ) : (
          <>
            <View
              ref={composerRef}
              style={[
                styles.composer,
                Platform.OS === "web" && styles.composerWeb,
                {
                  borderTopColor: cleanComposer ? "transparent" : c.border,
                  backgroundColor: cleanComposer ? "transparent" : c.surface,
                },
              ]}
            >
              {replyingTo ? (
                <View
                  style={[
                    styles.replyComposer,
                    { backgroundColor: c.background, borderColor: c.border },
                  ]}
                >
                  <View
                    style={[styles.replyAccent, { backgroundColor: c.tint }]}
                  />
                  <View style={styles.replyComposerCopy}>
                    <ThemedText variant="caption" tone="tint" numberOfLines={1}>
                      {t(strings.replyingToAuthor, {
                        author: replyAuthorLabel(replyingTo, currentUserId, t),
                      })}
                    </ThemedText>
                    <ThemedText
                      variant="caption"
                      tone="muted"
                      numberOfLines={1}
                    >
                      {replyingTo.content}
                    </ThemedText>
                  </View>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t(strings.cancelReplyA11y)}
                    onPress={() => setReplyingTo(null)}
                    hitSlop={8}
                    style={styles.cancelReply}
                  >
                    <Ionicons name="close" size={17} color={c.textMuted} />
                  </Pressable>
                </View>
              ) : null}

              <View style={styles.inputBar}>
                <TextInput
                  ref={composerInputRef}
                  value={text}
                  onChangeText={handleTextChange}
                  placeholder={t(strings.messagePlaceholder)}
                  placeholderTextColor={c.textMuted}
                  style={[
                    styles.input,
                    {
                      color: c.text,
                      backgroundColor: c.surface,
                      borderColor: lightMode ? c.tint : c.border,
                    },
                  ]}
                  multiline
                  returnKeyType="send"
                  // Load-bearing next to `multiline`, whose default submit
                  // behaviour is "newline" (TextInput.js:559): the keyboard's
                  // Send key inserted "\n" into the field, and on Android that
                  // insert landed AFTER `send()` had cleared the state, so
                  // `onChangeText` resurrected the sent text. "submit" fires
                  // `onSubmitEditing`, inserts nothing, and keeps focus.
                  submitBehavior="submit"
                  onSubmitEditing={() => send(text)}
                />
                <Pressable
                  onPress={() => send(text)}
                  disabled={!text.trim()}
                  accessibilityRole="button"
                  style={[
                    styles.sendBtn,
                    { backgroundColor: c.tint },
                    !text.trim() && styles.sendBtnDisabled,
                  ]}
                >
                  <Ionicons name="arrow-up" size={20} color={c.onTint} />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.deckOpenA11y)}
                  onPress={openDeck}
                  style={[
                    styles.deckOpenBtn,
                    {
                      backgroundColor: c.backgroundSelected,
                      // The grey hairline made this read as the disabled send
                      // button beside it (tint at 0.45 opacity ≈ this pale
                      // blue). A solid ring marks it as live — in the DARKER
                      // brand blue, because the plain tint ring still sat too
                      // close to the washed-out disabled send.
                      borderColor: lightMode ? c.tintStrong : c.border,
                    },
                  ]}
                >
                  <Ionicons
                    name="albums-outline"
                    size={20}
                    color={lightMode ? c.tintStrong : c.tint}
                  />
                </Pressable>
              </View>
            </View>
          </>
        )}
      </KeyboardAvoidingContainer>

      <ConversationDeckSheet
        visible={deckOpen}
        hasPlus={hasPlus}
        onLockedCategory={() => setPlusUpsellOpen(true)}
        category={deckCategory}
        suggestion={deckSuggestion}
        drawing={deckDrawing}
        promptText={deckSuggestion?.text ?? ""}
        followUpText={deckSuggestion?.followUpText ?? ""}
        draftConflict={Boolean(deckDraftInsert)}
        onClose={closeDeck}
        onCategoryChange={selectDeckCategory}
        onAddToMessage={addDeckPromptToMessage}
        onAnother={showAnotherDeckPrompt}
        onReplaceDraft={() => insertDeckDraft("replace")}
        onAddAfterDraft={() => insertDeckDraft("append")}
        onCancelDraft={() => setDeckDraftInsert(null)}
      />

      <PlusDeckUpsell
        visible={plusUpsellOpen}
        onDismiss={() => setPlusUpsellOpen(false)}
      />

      <Modal
        visible={endMenuOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setEndMenuOpen(false)}
      >
        <View style={styles.modalScreen}>
          <BlurredBackdrop
            accessibilityLabel={t(strings.closeOptionsA11y)}
            style={styles.modalBackdrop}
            onPress={() => setEndMenuOpen(false)}
          />

          <View
            style={[
              styles.endSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <View style={styles.endSheetHeader}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.close)}
                onPress={() => setEndMenuOpen(false)}
                hitSlop={10}
                style={[
                  styles.endClose,
                  { backgroundColor: c.backgroundSelected },
                ]}
              >
                <Ionicons name="close" size={20} color={c.textMuted} />
              </Pressable>
            </View>

            {/*
              The row set varies (reveal only pre-reveal, photo states change),
              so the sheet is capped and scrolls inside instead of growing to
              fit — its height stays put no matter which rows are present. A
              plain ScrollView on purpose: this is a fixed menu of a handful
              of options, not data that grows.
            */}
            <ScrollView
              bounces={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.endSheetScrollContent}
            >
            {!revealed && Platform.OS !== "web" ? (
              <Pressable
                disabled={revealing || revealRequestPending}
                onPress={() => {
                  setEndMenuOpen(false);
                  requestReveal();
                }}
                style={[
                  styles.endOption,
                  { borderColor: c.border, backgroundColor: c.background },
                ]}
              >
                <Ionicons
                  name={
                    revealRequestPending
                      ? "time-outline"
                      : revealRequestReceived
                        ? "checkmark-circle-outline"
                        : "arrow-redo-outline"
                  }
                  size={18}
                  color={c.tint}
                />
                <View style={styles.endOptionCopy}>
                  <ThemedText variant="bodyStrong" tone="tint">
                    {t(revealActionLabel)}
                  </ThemedText>
                  <ThemedText variant="caption" tone="muted">
                    {t(
                      revealRequestPending
                        ? strings.revealPending
                        : revealRequestReceived
                          ? strings.revealReceived
                        : strings.revealDescription,
                    )}
                  </ThemedText>
                </View>
              </Pressable>
            ) : null}

            {/*
              Always present once the conversation is live, in every state.
              After a completed exchange it stops being a button and just says
              so — a row that vanishes reads as something having gone wrong,
              and there is exactly one exchange per conversation to explain.
            */}
            <Pressable
              disabled={!photoActionable || photoBusy}
              onPress={() => {
                // A failed state read is retried in place, with the sheet left
                // open. Deliberately not chained into the picker: a browser
                // only opens a file dialog straight off a gesture, and an
                // awaited fetch in between forfeits that.
                if (photoUnreachable || !photo) {
                  void loadPhotoState();
                  return;
                }
                setEndMenuOpen(false);
                void startPhotoExchange();
              }}
              style={[
                styles.endOption,
                { borderColor: c.border, backgroundColor: c.background },
                !photoActionable && styles.endOptionInert,
              ]}
            >
              <Ionicons
                name={photoIcon}
                size={18}
                color={photoActionable ? c.tint : c.textMuted}
              />
              <View style={styles.endOptionCopy}>
                <ThemedText
                  variant="bodyStrong"
                  tone={photoActionable ? "tint" : "muted"}
                >
                  {/*
                    `photoWaiting` is "Waiting for {{name}}" — the only
                    parameterised label this row can resolve to. Without the
                    interpolation i18next renders the placeholder verbatim.
                    Passing it unconditionally is harmless for the labels that
                    take no parameters.
                  */}
                  {t(photoActionLabel, { name: displayName })}
                </ThemedText>
                <ThemedText variant="caption" tone="muted">
                  {t(photoActionHint)}
                </ThemedText>
              </View>
            </Pressable>

            <Pressable
              disabled={ending}
              onPress={() => void clearConversation()}
              style={[
                styles.endOption,
                { borderColor: c.border, backgroundColor: c.background },
              ]}
            >
              <Ionicons name="trash-outline" size={18} color={c.text} />
              <View style={styles.endOptionCopy}>
                <ThemedText variant="bodyStrong">
                  {t(strings.clearMessages)}
                </ThemedText>
              </View>
            </Pressable>

            <Pressable
              disabled={ending}
              onPress={async () => {
                // Was immediate: one tap moved the conversation to Inactive
                // with no way back from the gesture. It is recoverable rather
                // than destructive, but it is still a decision about a person,
                // and the row no longer explains itself — so the explanation
                // moves into the ask, where it is read at the moment it
                // matters. Sheet first, then the question, for the same reason
                // as Block below.
                setEndMenuOpen(false);
                const ok = await confirm({
                  title: t(strings.exitQuietly),
                  message: t(strings.exitDescription),
                  confirmLabel: t(strings.exit),
                  cancelLabel: t(strings.cancel),
                });
                if (ok) void endConversation("quiet");
              }}
              style={[
                styles.endOption,
                { borderColor: c.border, backgroundColor: c.background },
              ]}
            >
              <Ionicons name="log-out-outline" size={18} color={c.text} />
              <View style={styles.endOptionCopy}>
                <ThemedText variant="bodyStrong">
                  {t(strings.exitQuietly)}
                </ThemedText>
              </View>
            </Pressable>

            <Pressable
              disabled={ending}
              onPress={async () => {
                // The sheet closes BEFORE the question is asked. Blocking is a
                // decision about a person, and it gets the screen to itself
                // rather than floating over a menu of other options — and on
                // Cancel the sheet stays gone, back to the conversation, not
                // back into the menu. (This ordering also predates the
                // `ConfirmDialog` mount-at-ask fix; without that fix, a
                // confirm raised under this sheet was invisible on web, which
                // is how "tap Block, nothing happens" shipped.) Same
                // sequencing as Report below.
                setEndMenuOpen(false);
                const ok = await confirm({
                  title: t(strings.blockTitle, { name: displayName }),
                  message: t(strings.blockDescription),
                  confirmLabel: t(strings.block),
                  cancelLabel: t(strings.cancel),
                  destructive: true,
                });
                if (ok) void endConversation("blocked");
              }}
              style={[
                styles.endOption,
                { borderColor: c.border, backgroundColor: c.background },
              ]}
            >
              <Ionicons name="ban-outline" size={18} color={c.danger} />
              <View style={styles.endOptionCopy}>
                <ThemedText variant="bodyStrong" style={{ color: c.danger }}>
                  {t(strings.block)}
                </ThemedText>
              </View>
            </Pressable>

            <Pressable
              disabled={ending}
              onPress={() => {
                setEndMenuOpen(false);
                setReportOpen(true);
              }}
              style={[
                styles.endOption,
                { borderColor: c.border, backgroundColor: c.background },
              ]}
            >
              <Ionicons name="flag-outline" size={18} color={c.danger} />
              <View style={styles.endOptionCopy}>
                <ThemedText variant="bodyStrong" style={{ color: c.danger }}>
                  {t(strings.report)}
                </ThemedText>
              </View>
            </Pressable>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/*
        Full view. Deleting is offered only on your own photo, and it removes
        it for both people — you are deleting the photo, not un-sharing it.
      */}
      <Modal
        visible={photoViewerAssetId != null}
        transparent
        animationType="fade"
        onRequestClose={() => setPhotoViewerAssetId(null)}
      >
        <View style={styles.photoViewer}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(strings.close)}
            style={StyleSheet.absoluteFill}
            onPress={() => setPhotoViewerAssetId(null)}
          />
          {photoViewerAssetId && id ? (
            <FullPhoto chatId={id} assetId={photoViewerAssetId} />
          ) : null}

          <View style={styles.photoViewerBar}>
            {photoViewerAssetId &&
            photo?.revealed?.myAssetId === photoViewerAssetId ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => void deleteMyPhoto(photoViewerAssetId)}
                style={styles.photoViewerAction}
              >
                <Ionicons name="trash-outline" size={18} color="#fff" />
                <ThemedText variant="caption" style={styles.photoViewerText}>
                  {t(strings.photoDelete)}
                </ThemedText>
              </Pressable>
            ) : photoViewerAssetId ? (
              /*
                Reporting is offered on their photo only — there is nothing to
                report about your own. Filing it takes the photo off this
                screen straight away.
              */
              <Pressable
                accessibilityRole="button"
                onPress={() => {
                  setReportPhotoAssetId(photoViewerAssetId);
                  setPhotoViewerAssetId(null);
                  setReportOpen(true);
                }}
                style={styles.photoViewerAction}
              >
                <Ionicons name="flag-outline" size={18} color="#fff" />
                <ThemedText variant="caption" style={styles.photoViewerText}>
                  {t(strings.report)}
                </ThemedText>
              </Pressable>
            ) : null}

            <Pressable
              accessibilityRole="button"
              onPress={() => setPhotoViewerAssetId(null)}
              style={styles.photoViewerAction}
            >
              <Ionicons name="close" size={18} color="#fff" />
              <ThemedText variant="caption" style={styles.photoViewerText}>
                {t(strings.close)}
              </ThemedText>
            </Pressable>
          </View>
        </View>
      </Modal>

      <ReportModal
        visible={reportOpen}
        reportedUserId={chat?.peerId ?? ""}
        chatId={id}
        photoAssetId={reportPhotoAssetId ?? undefined}
        onClose={() => {
          setReportOpen(false);
          setReportPhotoAssetId(null);
        }}
        onSubmitted={() => {
          setReportOpen(false);
          setReportPhotoAssetId(null);
          router.replace("/(tabs)/inbox");
        }}
      />
    </SafeAreaView>
  );
}

function ConversationDeckSheet({
  visible,
  category,
  suggestion,
  drawing,
  promptText,
  followUpText,
  draftConflict,
  onClose,
  onCategoryChange,
  onAddToMessage,
  onAnother,
  onReplaceDraft,
  onAddAfterDraft,
  onCancelDraft,
  hasPlus,
  onLockedCategory,
}: {
  visible: boolean;
  hasPlus: boolean;
  onLockedCategory: () => void;
  category: ConversationPromptCategory;
  suggestion: ConversationPromptSuggestion | null;
  /** A draw is in flight; the card shows a spinner instead of stale text. */
  drawing: boolean;
  promptText: string;
  followUpText: string;
  draftConflict: boolean;
  onClose: () => void;
  onCategoryChange: (category: ConversationPromptCategory) => void;
  onAddToMessage: () => void;
  onAnother: () => void;
  onReplaceDraft: () => void;
  onAddAfterDraft: () => void;
  onCancelDraft: () => void;
}) {
  const { t } = useTranslation();
  const c = useTheme();
  const scheme = useScheme();
  const deckColors = useMemo(
    () => getConversationDeckColors(c, scheme),
    [c, scheme],
  );
  const [categoryViewportWidth, setCategoryViewportWidth] = useState(0);
  const [categoryContentWidth, setCategoryContentWidth] = useState(0);
  const categoryRailKey = visible ? "open" : "closed";
  const [categoryEndState, setCategoryEndState] = useState({
    key: categoryRailKey,
    atEnd: false,
  });
  const categoryAtEnd =
    categoryEndState.key === categoryRailKey && categoryEndState.atEnd;
  const categoriesContinue =
    categoryContentWidth > 0 &&
    categoryViewportWidth > 0 &&
    categoryContentWidth > categoryViewportWidth + 2 &&
    !categoryAtEnd;
  const deckPromptBadge = t(DECK_PROMPT_BADGE_KEYS[category]);
  const renderCategoryPill = (item: ConversationPromptCategory) => {
    const selected = item === category;
    // A locked pill is still shown, and still says what it is. Hiding the
    // Plus decks would make the app look smaller rather than make the offer
    // legible, and someone cannot decide to pay for something they never see.
    const locked = !hasPlus && isPlusDeckCategory(item);
    const label = t(DECK_CATEGORY_LABEL_KEYS[item]);
    const iconName = locked ? "lock-closed-outline" : DECK_CATEGORY_ICON_NAMES[item];
    return (
      <Pressable
        key={item}
        accessibilityRole="button"
        accessibilityState={{ selected, disabled: locked }}
        accessibilityLabel={t(
          locked
            ? strings.deckPlusLockedA11y
            : selected
              ? strings.deckCategorySelectedA11y
              : strings.deckCategoryA11y,
          { category: label },
        )}
        onPress={() => {
          if (locked) {
            onLockedCategory();
            return;
          }
          onCategoryChange(item);
        }}
        style={[
          styles.deckCategoryPill,
          selected
            ? [
                styles.deckCategoryPillSelected,
                {
                  backgroundColor: deckColors.categorySelectedBackground,
                  borderColor: deckColors.categorySelectedBorder,
                  shadowColor: deckColors.categorySelectedShadow,
                },
              ]
            : {
                backgroundColor: deckColors.categoryIdleBackground,
                borderColor: deckColors.categoryIdleBorder,
              },
        ]}
      >
        <Ionicons
          name={iconName}
          size={15}
          color={
            selected
              ? deckColors.categorySelectedIcon
              : deckColors.categoryIdleIcon
          }
        />
        <ThemedText
          style={[
            styles.deckCategoryText,
            {
              color: selected
                ? deckColors.categorySelectedText
                : deckColors.categoryIdleText,
            },
          ]}
        >
          {label}
        </ThemedText>
      </Pressable>
    );
  };

  useEffect(() => {
    if (!visible) return;
    AccessibilityInfo.announceForAccessibility(t(strings.deckTitle));
  }, [t, visible]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        style={styles.modalScreen}
        accessibilityViewIsModal
        accessibilityLabel={t(strings.deckTitle)}
      >
        <BlurredBackdrop
          accessibilityLabel={t(strings.deckCloseA11y)}
          style={[styles.modalBackdrop, { backgroundColor: deckColors.backdrop }]}
          onPress={onClose}
        />

        <View
          style={[
            styles.deckSheet,
            {
              backgroundColor: deckColors.sheetBackground,
              borderColor: deckColors.sheetBorder,
              shadowColor: deckColors.sheetShadow,
            },
          ]}
        >
          <View
            style={[
              styles.deckHandle,
              { backgroundColor: deckColors.handle },
            ]}
          />
          <View style={styles.deckHeader}>
            <View style={styles.deckTitleCopy}>
              <ThemedText
                variant="title"
                style={[styles.deckTitle, { color: deckColors.title }]}
              >
                {t(strings.deckTitle)}
              </ThemedText>
              <ThemedText
                variant="body"
                style={[styles.deckSubtitle, { color: deckColors.subtitle }]}
              >
                {t(strings.deckSubtitle)}
              </ThemedText>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(strings.deckCloseA11y)}
              onPress={onClose}
              hitSlop={10}
              style={[
                styles.deckCloseBtn,
                { backgroundColor: deckColors.closeBackground },
              ]}
            >
              <Ionicons name="close" size={23} color={deckColors.closeIcon} />
            </Pressable>
          </View>

          <View style={styles.deckCategoryArea}>
            <ScrollView
              key={categoryRailKey}
              horizontal
              showsHorizontalScrollIndicator={false}
              onLayout={(event) =>
                setCategoryViewportWidth(event.nativeEvent.layout.width)
              }
              onContentSizeChange={(width) => setCategoryContentWidth(width)}
              onScroll={(event) => {
                const offset = event.nativeEvent.contentOffset.x;
                const atEnd =
                  offset + categoryViewportWidth >= categoryContentWidth - 2;
                setCategoryEndState((current) => {
                  if (
                    current.key === categoryRailKey &&
                    current.atEnd === atEnd
                  ) {
                    return current;
                  }
                  return { key: categoryRailKey, atEnd };
                });
              }}
              scrollEventThrottle={16}
              contentContainerStyle={styles.deckCategoryList}
              style={styles.deckCategoryScroll}
            >
              {CONVERSATION_PROMPT_CATEGORIES.map(renderCategoryPill)}
            </ScrollView>
            {categoriesContinue ? (
              <View
                pointerEvents="none"
                style={[
                  styles.deckCategoryPeek,
                  { backgroundColor: deckColors.sheetBackground },
                ]}
              />
            ) : null}
          </View>

          {draftConflict ? (
            <View
              style={[
                styles.deckDraftCard,
                {
                  backgroundColor: deckColors.draftBackground,
                  borderColor: deckColors.draftBorder,
                },
              ]}
            >
              <ThemedText
                variant="bodyStrong"
                style={[styles.deckDraftTitle, { color: deckColors.draftTitle }]}
              >
                {t(strings.deckDraftTitle)}
              </ThemedText>
              <ThemedText
                variant="caption"
                style={[styles.deckDraftBody, { color: deckColors.draftBody }]}
              >
                {t(strings.deckDraftBody)}
              </ThemedText>
              <View style={styles.deckActions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.deckReplaceDraftA11y)}
                  onPress={onReplaceDraft}
                  style={[
                    styles.deckActionBtn,
                    styles.deckActionPrimary,
                    styles.deckAddBtn,
                    {
                      backgroundColor: deckColors.primaryBackground,
                      borderColor: deckColors.primaryBorder,
                    },
                  ]}
                >
                  <ThemedText
                    variant="bodyStrong"
                    numberOfLines={2}
                    style={[
                      styles.deckActionLabel,
                      styles.deckAddBtnText,
                      { color: deckColors.primaryText },
                    ]}
                  >
                    {t(strings.deckReplaceDraft)}
                  </ThemedText>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.deckAddAfterDraftA11y)}
                  onPress={onAddAfterDraft}
                  style={[
                    styles.deckActionBtn,
                    styles.deckSecondaryBtn,
                    {
                      backgroundColor: deckColors.secondaryBackground,
                      borderColor: deckColors.secondaryBorder,
                    },
                  ]}
                >
                  <ThemedText
                    variant="bodyStrong"
                    numberOfLines={2}
                    style={[
                      styles.deckActionLabel,
                      styles.deckSecondaryText,
                      { color: deckColors.secondaryText },
                    ]}
                  >
                    {t(strings.deckAddAfterDraft)}
                  </ThemedText>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  onPress={onCancelDraft}
                  style={[
                    styles.deckActionBtn,
                    styles.deckGhostBtn,
                    { borderColor: deckColors.ghostBorder },
                  ]}
                >
                  <ThemedText
                    variant="bodyStrong"
                    numberOfLines={2}
                    style={[
                      styles.deckActionLabel,
                      styles.deckGhostText,
                      { color: deckColors.ghostText },
                    ]}
                  >
                    {t(strings.deckCancelDraft)}
                  </ThemedText>
                </Pressable>
              </View>
            </View>
          ) : (
            <>
              <View
                style={[
                  styles.deckPromptCard,
                  {
                    backgroundColor: deckColors.promptBackground,
                    borderColor: deckColors.promptBorder,
                  },
                ]}
              >
                {/*
                  The card's height is fixed (styles.deckPromptCard), so the
                  sheet does not resize on every "Another" as prompts change
                  length; a prompt too tall for the card scrolls within it.
                */}
                <ScrollView
                  bounces={false}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.deckPromptCardContent}
                >
                {drawing ? (
                  // The "Good for …" badge is loading-time copy only: it names
                  // what is being drawn while the spinner runs. Once the
                  // prompt is on screen it says the same thing the prompt
                  // already shows, so the loaded card drops it — the category
                  // rail above stays the place that names the selection.
                  <>
                    <View style={styles.deckPromptBadgeRow}>
                      <ThemedText
                        style={[
                          styles.deckPromptBadgeText,
                          { color: deckColors.promptBadge },
                        ]}
                      >
                        {deckPromptBadge}
                      </ThemedText>
                    </View>
                    <View style={styles.deckPromptLoading}>
                      <ActivityIndicator
                        size="small"
                        color={deckColors.promptBadge}
                      />
                    </View>
                  </>
                ) : (
                <ThemedText
                  variant="title"
                  style={[styles.deckPromptText, { color: deckColors.promptText }]}
                >
                  {promptText}
                </ThemedText>
                )}

                {!drawing && followUpText ? (
                  <View
                    style={[
                      styles.deckFollowUp,
                      { borderTopColor: deckColors.divider },
                    ]}
                  >
                    <View
                      style={[
                        styles.deckFollowUpIcon,
                        { backgroundColor: deckColors.followUpIconBackground },
                      ]}
                    >
                      <Ionicons
                        name="return-down-forward-outline"
                        size={16}
                        color={deckColors.followUpIcon}
                      />
                    </View>
                    <View
                      accessible
                      accessibilityRole="text"
                      accessibilityLabel={t(strings.deckFollowUpA11y, {
                        prompt: followUpText,
                      })}
                      style={styles.deckFollowUpCopy}
                    >
                      <View style={styles.deckFollowUpHeader}>
                        <ThemedText
                          variant="caption"
                          style={[
                            styles.deckFollowUpLabel,
                            { color: deckColors.followUpLabel },
                          ]}
                        >
                          {t(strings.deckFollowUpPossibleIfAnswered)}
                        </ThemedText>
                      </View>
                      <ThemedText
                        variant="body"
                        style={[
                          styles.deckFollowUpText,
                          { color: deckColors.followUpText },
                        ]}
                      >
                        {followUpText}
                      </ThemedText>
                    </View>
                  </View>
                ) : null}
                </ScrollView>
              </View>

              <View
                style={styles.deckActions}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.deckAddToMessageA11y)}
                  onPress={onAddToMessage}
                  disabled={!suggestion || drawing}
                  style={[
                    styles.deckActionBtn,
                    styles.deckActionPrimary,
                    styles.deckAddBtn,
                    {
                      backgroundColor: deckColors.primaryBackground,
                      borderColor: deckColors.primaryBorder,
                    },
                    // Also while drawing: `suggestion` still holds the
                    // PREVIOUS prompt then, and Add would insert text the
                    // card is no longer showing.
                    (!suggestion || drawing) && styles.sendBtnDisabled,
                  ]}
                >
                  <Ionicons name="add" size={19} color={deckColors.primaryIcon} />
                  <ThemedText
                    variant="bodyStrong"
                    numberOfLines={2}
                    style={[
                      styles.deckActionLabel,
                      styles.deckAddBtnText,
                      { color: deckColors.primaryText },
                    ]}
                  >
                    {t(strings.deckAddToMessage)}
                  </ThemedText>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.deckAnotherA11y)}
                  onPress={onAnother}
                  style={[
                    styles.deckActionBtn,
                    styles.deckSecondaryBtn,
                    {
                      backgroundColor: deckColors.secondaryBackground,
                      borderColor: deckColors.secondaryBorder,
                    },
                  ]}
                >
                  <Ionicons
                    name="shuffle-outline"
                    size={16}
                    color={deckColors.secondaryIcon}
                  />
                  <ThemedText
                    variant="bodyStrong"
                    numberOfLines={2}
                    style={[
                      styles.deckActionLabel,
                      styles.deckSecondaryText,
                      { color: deckColors.secondaryText },
                    ]}
                  >
                    {t(strings.deckAnother)}
                  </ThemedText>
                </Pressable>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.deckPhraseMyself)}
                onPress={onClose}
                style={styles.deckPhraseMyself}
              >
                <Ionicons
                  name="chatbubble-ellipses-outline"
                  size={15}
                  color={deckColors.phraseIcon}
                />
                <ThemedText
                  style={[
                    styles.deckPhraseMyselfText,
                    { color: deckColors.phraseText },
                  ]}
                >
                  {t(strings.deckPhraseMyself)}
                </ThemedText>
              </Pressable>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

function TypingIndicator({
  visible,
  avatarSeed,
  avatarConfig,
  name,
  colors,
}: {
  visible: boolean;
  avatarSeed: string;
  avatarConfig?: UserAvatarConfig | null;
  name: string;
  colors: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    <View
      accessibilityRole="text"
      accessibilityLabel={t(strings.typingA11y, { name })}
      style={styles.typingIndicatorRow}
    >
      <Avatar
        seed={avatarSeed}
        avatarConfig={avatarConfig}
        name={name}
        size={24}
        // Too small for a badge to be read at all, and this chip is a
        // transient "they're typing" cue, not an introduction.
        showBadge={false}
      />
      <View
        style={[
          styles.typingBubble,
          {
            backgroundColor: colors.backgroundSelected,
            borderColor: colors.border,
          },
        ]}
      >
        {[0, 1, 2].map((dot) => (
          <View
            key={dot}
            style={[styles.typingDot, { backgroundColor: colors.textMuted }]}
          />
        ))}
      </View>
    </View>
  );
}

/** The full-size photo inside the viewer, resolved for the platform. */
function FullPhoto({ chatId, assetId }: { chatId: string; assetId: string }) {
  const resolved = useAuthedImage(chatApi.photoSource(chatId, assetId, "full"));
  if (!resolved.source) return null;

  return (
    <Image
      source={resolved.source}
      style={styles.photoViewerImage}
      resizeMode="contain"
    />
  );
}

/**
 * A photo inside a bubble.
 *
 * The image is fetched from the authenticated API with the token attached, so
 * it stops resolving the moment the photo is deleted or reported — there is no
 * link that could keep working after access is revoked. A failure is therefore
 * expected rather than exceptional.
 *
 * But only the server knows which failure it is, so only the server's answer
 * gets to say the photo is gone. A 401, a dropped connection or a laptop that
 * went to sleep leave the photo exactly where it was, and telling the reader it
 * "is no longer available" was both false and a dead end — there was nothing to
 * do but reload the page. Those are offered as a retry instead.
 */
function PhotoBubble({
  chatId,
  assetId,
  photoWidth,
  photoHeight,
  onOpen,
}: {
  chatId: string;
  assetId: string | null;
  photoWidth?: number | null;
  photoHeight?: number | null;
  onOpen: (assetId: string) => void;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  // Keyed to the source so a decode failure on one photo does not outlive it.
  // As a bare boolean this latched for the life of the bubble, which kept the
  // tombstone up even once the retry had the bytes in hand.
  const [decodeFailed, setDecodeFailed] = useState<string | null>(null);
  const { width: windowWidth } = useWindowDimensions();
  const resolved = useAuthedImage(
    assetId ? chatApi.photoSource(chatId, assetId, "thumb") : null,
  );

  // Cut to the photo, and to what this window can spare. Computed from the
  // dimensions the message carried, so the box is right on the first frame —
  // nothing resizes under the reader once the bytes land.
  // Zero padding, because the bubble around a photo has none — the picture runs
  // to its edges, so every point the bubble occupies is picture.
  const box = photoBox(
    photoWidth,
    photoHeight,
    availablePhotoWidth(windowWidth, CHAT_MAX_WIDTH, 0.78, 0),
  );

  const sourceUri =
    resolved.source && "uri" in resolved.source ? resolved.source.uri : null;
  const broken = sourceUri !== null && decodeFailed === sourceUri;

  if (!assetId || resolved.gone) {
    return (
      <View style={[styles.photoGone, { borderColor: c.border }]}>
        <Ionicons name="image-outline" size={18} color={c.textMuted} />
        <ThemedText variant="caption" tone="muted">
          {t(strings.photoUnavailable)}
        </ThemedText>
      </View>
    );
  }

  // Not the photo's fault, and not a dead end: it retries itself for a while,
  // and stays tappable after it gives up.
  if (resolved.unreachable || broken) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={resolved.retry}
        style={[styles.photoGone, { borderColor: c.border }]}
      >
        <Ionicons name="refresh-outline" size={18} color={c.textMuted} />
        <ThemedText variant="caption" tone="muted">
          {t(strings.photoRetry)}
        </ThemedText>
      </Pressable>
    );
  }

  // On web the bytes are still being fetched; hold the space rather than
  // flashing anything at a photo that is about to arrive. The placeholder is
  // the same box the photo will occupy, so nothing moves when it does.
  if (!resolved.source) return <View style={[styles.photoThumb, box]} />;

  return (
    <Pressable
      accessibilityRole="imagebutton"
      accessibilityLabel={t(strings.photoOpenA11y)}
      onPress={() => onOpen(assetId)}
    >
      <Image
        source={resolved.source}
        style={[styles.photoThumb, box]}
        // The box already matches the photo's shape, so `cover` and `contain`
        // draw the same pixels. `contain` is the safer of the two to state: on
        // a photo whose dimensions the server never recorded, the box is a
        // guess, and this shows all of it rather than cropping to fit a guess.
        resizeMode="contain"
        onError={() => setDecodeFailed(sourceUri)}
      />
    </Pressable>
  );
}

function Bubble({
  message,
  currentUserId,
  reactionPickerOpen,
  onToggleReactionPicker,
  onReact,
  onReply,
  onOpenPhoto,
}: {
  message: ChatMessage;
  currentUserId?: string;
  reactionPickerOpen: boolean;
  onToggleReactionPicker: () => void;
  onReact: (reaction: string) => void;
  onReply: () => void;
  onOpenPhoto: (assetId: string) => void;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  const mine = message.senderId === "me" || message.senderId === currentUserId;
  const time = formatMessageTime(message.timestamp);
  const dragX = useMemo(() => new Animated.Value(0), []);
  const customReactionInputRef = useRef<TextInput>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const resetSwipe = useCallback(() => {
    Animated.spring(dragX, {
      toValue: 0,
      useNativeDriver: true,
      bounciness: 7,
      speed: 22,
    }).start();
  }, [dragX]);
  const replyHintOpacity = dragX.interpolate({
    inputRange: [0, 28, 72],
    outputRange: [0, 0.7, 1],
    extrapolate: "clamp",
  });
  const replyHintScale = dragX.interpolate({
    inputRange: [0, 72],
    outputRange: [0.78, 1],
    extrapolate: "clamp",
  });
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_, gesture) =>
          gesture.dx > 14 && Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.35,
        onPanResponderGrant: () => {
          dragX.stopAnimation();
        },
        onPanResponderMove: (_, gesture) => {
          dragX.setValue(Math.max(0, Math.min(gesture.dx, 76)));
        },
        onPanResponderRelease: (_, gesture) => {
          if (gesture.dx > 54 && Math.abs(gesture.dy) < 42) onReply();
          resetSwipe();
        },
        onPanResponderTerminate: resetSwipe,
      }),
    [dragX, onReply, resetSwipe],
  );
  // Apply the emoji the user picked from the system keyboard, then dismiss it.
  // Fires per input event, so a single tapped emoji is applied immediately.
  const onCustomReactionChange = useCallback(
    (text: string) => {
      // Take the FIRST emoji and ignore anything else. This field is reachable
      // from a full keyboard, so it used to send whatever was typed — letters
      // included — and a reaction renders inside the other person's
      // conversation. The server enforces the same rule and is the authority;
      // this keeps a stray keystroke from becoming a rejected request.
      const reaction = firstEmoji(text);
      if (!reaction) return;
      customReactionInputRef.current?.blur();
      onReact(reaction);
    },
    [onReact],
  );
  const openMoreReactions = useCallback(() => {
    // Web: our own picker, because the OS one is unreachable from a browser.
    // Native: the real system keyboard, which is strictly better where it
    // exists — it carries the user's recents, skin-tone choices and search.
    if (Platform.OS === "web") {
      setEmojiPickerOpen(true);
      return;
    }

    customReactionInputRef.current?.focus();
  }, []);
  return (
    <View
      style={[styles.bubbleRow, mine ? styles.bubbleRight : styles.bubbleLeft]}
      {...panResponder.panHandlers}
    >
      <Animated.View
        pointerEvents="none"
        style={[
          styles.replyDragHint,
          {
            backgroundColor: c.backgroundSelected,
            opacity: replyHintOpacity,
            transform: [{ scale: replyHintScale }],
          },
        ]}
      >
        <Ionicons name="arrow-undo" size={17} color={c.tint} />
      </Animated.View>

      <Animated.View
        style={[
          styles.messageStack,
          mine && styles.messageStackRight,
          { transform: [{ translateX: dragX }] },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.reactA11y)}
          onPress={onToggleReactionPicker}
          style={[
            styles.bubble,
            // A photo fills its bubble edge to edge. Padded, the picture was
            // inset on all four sides by bubble chrome that carried nothing —
            // the same footprint holding a visibly smaller image.
            message.kind === "photo" && styles.bubblePhoto,
            {
              backgroundColor: mine ? c.tint : c.surface,
              borderColor: c.border,
              borderWidth: mine ? 0 : 1,
              borderBottomRightRadius: mine ? 4 : Radius.lg,
              borderBottomLeftRadius: mine ? Radius.lg : 4,
            },
          ]}
        >
          {message.replyTo ? (
            <View
              style={[
                styles.replyPreview,
                // The bubble around a photo has no padding, so a quote inside
                // one has to bring its own or it touches the edges.
                message.kind === "photo" && styles.replyPreviewInPhoto,
                {
                  backgroundColor: mine
                    ? "rgba(255,255,255,0.18)"
                    : c.backgroundSelected,
                  borderLeftColor: mine ? c.onTint : c.tint,
                },
              ]}
            >
              <ThemedText
                variant="caption"
                style={{
                  color: mine ? c.onTint : c.tint,
                  fontWeight: "800",
                }}
                numberOfLines={1}
              >
                {replyAuthorLabel(message.replyTo, currentUserId, t)}
              </ThemedText>
              <ThemedText
                variant="caption"
                style={{ color: mine ? c.onTint : c.textMuted }}
                numberOfLines={1}
              >
                {message.replyTo.content}
              </ThemedText>
            </View>
          ) : null}
          {message.kind === "photo" ? (
            <PhotoBubble
              chatId={message.chatId}
              assetId={message.photoAssetId ?? null}
              photoWidth={message.photoWidth}
              photoHeight={message.photoHeight}
              onOpen={onOpenPhoto}
            />
          ) : (
            <ThemedText
              variant="body"
              style={{ color: mine ? c.onTint : c.text }}
            >
              {/* A line the APP wrote carries a key and is rendered in the
                  reader's language; a line a PERSON wrote has none and is
                  shown exactly as they typed it. One call covers both — the
                  key is what tells them apart. */}
              {systemMessageText({
                content: message.content,
                systemKey: message.systemKey,
              })}
            </ThemedText>
          )}
        </Pressable>

        {time || message.reaction ? (
          <View style={styles.messageMetaRow}>
            {message.reaction ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.removeReactionA11y)}
                onPress={() => {
                  if (message.reaction) onReact(message.reaction);
                }}
                style={({ pressed }) => [
                  styles.reactionPill,
                  {
                    backgroundColor: c.backgroundSelected,
                    borderColor: c.border,
                  },
                  pressed && styles.reactionPillPressed,
                ]}
              >
                <ThemedText style={styles.reactionPillText}>
                  {message.reaction}
                </ThemedText>
              </Pressable>
            ) : null}

            {time ? (
              // A FIXED slot, and the whole fix for the shake filmed on build
              // 13. The timestamp is ALWAYS rendered, invisible while the
              // message is pending, so the slot's height and width are decided
              // once by the text that will finally occupy it. Nothing about
              // the pending-to-sent transition can move a pixel: an inverted
              // thread turns any bubble resize into every message above it
              // jumping.
              //
              // The previous version swapped an icon for the timestamp and
              // trusted them to measure alike. They do not — `create-icon-set`
              // applies our `style` AFTER its own `fontSize`, so the glyph
              // renders at the caption's numbers but in the ICON font, whose
              // ascent and descent are its own. Matching two fonts by eye is
              // not a guarantee; removing the degree of freedom is.
              <View style={styles.messageTimeSlot}>
                <ThemedText
                  variant="caption"
                  tone="muted"
                  style={[
                    styles.messageTime,
                    message.pending && styles.messageTimeReserved,
                  ]}
                  // Invisible is not inaudible: without this a screen reader
                  // would announce a send time for a message that has not
                  // been sent.
                  accessibilityElementsHidden={message.pending}
                  importantForAccessibility={
                    message.pending ? "no-hide-descendants" : "auto"
                  }
                >
                  {time}
                </ThemedText>

                {message.pending && message.pendingSlow ? (
                  // The WhatsApp affordance, now shown only once a send has
                  // taken long enough to be worth mentioning
                  // (`SLOW_SEND_AFTER_MS`). Measured sends land in 120-170ms,
                  // so a healthy message shows no marker at all. One marker
                  // for both live states — queued and in-flight look identical
                  // on purpose, and there is no failed appearance to reach
                  // (`outbox-core.ts`).
                  //
                  // Absolutely positioned over the reserved text so raising it
                  // cannot change the slot either.
                  <Ionicons
                    name="time-outline"
                    size={12}
                    color={c.textMuted}
                    style={styles.messageTimeMarker}
                    accessibilityLabel={t(strings.messagePending)}
                  />
                ) : null}
              </View>
            ) : null}
          </View>
        ) : null}

        {reactionPickerOpen ? (
          <View
            onTouchEnd={(event) => event.stopPropagation()}
            style={[
              styles.reactionPicker,
              {
                backgroundColor: c.surface,
                borderColor: c.border,
              },
            ]}
          >
            {MESSAGE_REACTIONS.map((reaction) => {
              const selected = message.reaction === reaction;
              return (
                <Pressable
                  key={reaction}
                  accessibilityRole="button"
                  accessibilityLabel={t(strings.reactWithA11y, { reaction })}
                  onPress={() => onReact(reaction)}
                  style={[
                    styles.reactionButton,
                    selected && { backgroundColor: c.backgroundSelected },
                  ]}
                >
                  <ThemedText style={styles.reactionButtonText}>
                    {reaction}
                  </ThemedText>
                </Pressable>
              );
            })}

            {/*
              One affordance on both platforms now. It used to expand a second
              hardcoded palette of twelve on web — a workaround for having no
              device emoji there, which the picker replaces with the whole set.
            */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(strings.pickEmojiA11y)}
              onPress={openMoreReactions}
              style={[
                styles.reactionButton,
                Platform.OS !== "web" && {
                  backgroundColor: c.backgroundSelected,
                },
              ]}
            >
              <Ionicons name="add" size={20} color={c.tint} />
            </Pressable>

            {/*
              Native only: invisible, and its whole job is raising the system
              keyboard when "+" focuses it. Web gets the in-app picker below
              instead — a browser cannot open the OS emoji picker (⌃⌘Space and
              Win+. are OS shortcuts with no API), and a visible field the user
              has to type into puts that limitation in front of them rather
              than solving it.
            */}
            {Platform.OS === "web" ? null : (
              <TextInput
                ref={customReactionInputRef}
                value=""
                onChangeText={onCustomReactionChange}
                autoCapitalize="none"
                autoCorrect={false}
                caretHidden
                blurOnSubmit
                style={styles.hiddenReactionInput}
              />
            )}
          </View>
        ) : null}

        {/*
          Web's replacement for the system keyboard. Rendered inside the row so
          it can react to THIS message; a Modal, so it is not clipped by the
          message list. Selections go through `onCustomReactionChange` like the
          native ones, which is what applies the single-emoji rule.
        */}
        {Platform.OS === "web" ? (
          <EmojiPicker
            visible={emojiPickerOpen}
            onSelect={onCustomReactionChange}
            onClose={() => setEmojiPickerOpen(false)}
          />
        ) : null}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  flex: { flex: 1 },
  header: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
  },
  // Full-width top navbar on web (mirrors the inbox top bar).
  headerWeb: {
    maxWidth: "100%",
    alignSelf: "stretch",
    minHeight: 62,
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 12,
  },
  back: { marginRight: Spacing.xs / 2 },
  headerPerson: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  headerPersonPressed: {
    opacity: 0.78,
  },
  headerAvatar: { position: "relative" },
  // Scaled from the inbox row's indicator (14 on a 54px avatar) so the two
  // read as the same mark.
  headerOnlineDot: {
    position: "absolute",
    right: 0,
    bottom: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
  },
  headerInfo: { flex: 1 },
  headerNameRow: { flexDirection: "row", alignItems: "center", gap: 5 },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
  },
  revealBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.pill,
  },
  revealText: { fontWeight: "700" },
  optionsBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  requestSheet: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    gap: 4,
    paddingLeft: Spacing.lg,
    paddingRight: Spacing.md,
    paddingVertical: 7,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  requestSheetClose: {
    position: "absolute",
    top: 5,
    right: BANNER_CLOSE_INSET,
    width: BANNER_CLOSE_SIZE,
    height: BANNER_CLOSE_SIZE,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: BANNER_CLOSE_SIZE / 2,
    // The close is the FIRST child, so every row after it is a later sibling
    // painted on top. On native that costs nothing — a plain View does not
    // capture touches — but on web the overlapping DOM element swallows the
    // click, and the X did nothing. zIndex lifts it back above them.
    zIndex: 2,
    elevation: 2,
  },
  requestSheetRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.md,
    minHeight: 28,
  },
  requestSheetMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  requestSheetCopy: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  requestSheetTitle: {
    fontWeight: "700",
    lineHeight: 18,
  },
  requestSheetHint: {
    lineHeight: 17,
  },
  requestSheetDivider: {
    height: StyleSheet.hairlineWidth,
    marginLeft: 25,
    marginVertical: 1,
    opacity: 0.72,
  },
  requestSheetStack: {
    gap: 6,
  },
  requestSheetCloseSpacer: {
    // Only as wide as the close actually is — the old 132px rail was sized for
    // the buttons that now live on their own row.
    width: BANNER_CLOSE_FOOTPRINT,
  },
  requestSheetActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
    // Wrap is kept as a narrow-screen safety net, but with a full-width row
    // rather than a 132px rail these two pills fit side by side.
    flexWrap: "wrap",
    justifyContent: "center",
  },
  requestSheetActionRail: {
    width: 132,
    minHeight: 28,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  // Stops the rail's contents short of the close. The row's own padding is
  // already inside the close's footprint, so only the difference is needed —
  // plus a gap, so the pill and the X do not read as one control.
  requestSheetRailClearsClose: {
    marginRight: BANNER_CLOSE_FOOTPRINT - Spacing.md + Spacing.xs,
  },
  requestSheetButton: {
    minHeight: 28,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.md,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  requestSheetPrimaryButton: {
    minWidth: 86,
  },
  lifecycleBanner: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderTopWidth: 1,
  },
  lifecycleBannerText: {
    flex: 1,
    fontWeight: "600",
  },
  reopenCard: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    gap: Spacing.md,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderTopWidth: 1,
  },
  reopenCopy: {
    gap: 4,
  },
  reopenActions: {
    flexDirection: "row",
    gap: Spacing.sm,
  },
  reopenButton: {
    flex: 1,
    minHeight: 38,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  // On its own the button has no sibling to share the row with, so `flex: 1`
  // would stretch it edge to edge and read as a confirmation, not an ask.
  reopenSoloButton: {
    flex: 0,
    alignSelf: "flex-start",
    paddingHorizontal: Spacing.xl,
  },
  endOptionInert: {
    opacity: 0.6,
  },
  // Width and height come from `photoBox` at render time — they depend on the
  // photo's own shape and on how much room the window has, neither of which a
  // stylesheet knows.
  photoThumb: {
    // No radius of its own: the bubble clips it, so the photo takes the
    // bubble's corners — including the squared-off one on the sender's side.
    backgroundColor: "rgba(127,127,127,0.15)",
  },
  photoGone: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderStyle: "dashed",
  },
  photoViewer: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.94)",
    justifyContent: "center",
  },
  photoViewerImage: {
    width: "100%",
    height: "80%",
  },
  photoViewerBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "center",
    gap: Spacing.xl,
    paddingVertical: Spacing.xl,
  },
  photoViewerAction: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
  },
  photoViewerText: {
    color: "#fff",
  },
  reopenPrimaryButton: {
    borderWidth: 0,
  },
  reopenPrimaryText: {
    fontWeight: "800",
  },
  loadEarlier: {
    alignSelf: "center",
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
  },
  // Dimmed rather than swapped for a spinner: the row keeps its height, so the
  // thread does not jump under the reader while the page is fetched.
  loadEarlierBusy: {
    opacity: 0.5,
  },
  messages: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    padding: Spacing.lg,
    gap: Spacing.sm,
    flexGrow: 1,
  },
  intro: { flex: 1, justifyContent: "center", paddingVertical: Spacing.xxxl },
  typingIndicatorRow: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: Spacing.lg,
    paddingBottom: Spacing.xs,
  },
  typingBubble: {
    minWidth: 42,
    height: 26,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    borderRadius: 13,
    borderWidth: 1,
  },
  typingDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    opacity: 0.72,
  },
  bubbleRow: { flexDirection: "row", position: "relative" },
  bubbleLeft: { justifyContent: "flex-start" },
  bubbleRight: { justifyContent: "flex-end" },
  replyDragHint: {
    position: "absolute",
    left: 10,
    top: "50%",
    width: 34,
    height: 34,
    marginTop: -17,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
  },
  messageStack: {
    maxWidth: "78%",
    alignItems: "flex-start",
    gap: 4,
  },
  messageStackRight: {
    alignItems: "flex-end",
  },
  bubble: {
    maxWidth: "100%",
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: Radius.lg,
    gap: 6,
  },
  // The picture IS the message: no padding, and clipped to the bubble's own
  // corners so the image takes the shape the bubble would have had.
  bubblePhoto: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    gap: 0,
    overflow: "hidden",
  },
  replyPreviewInPhoto: {
    marginHorizontal: Spacing.lg,
    marginTop: Spacing.md,
    marginBottom: 6,
  },
  replyPreview: {
    minWidth: 150,
    paddingHorizontal: Spacing.sm,
    paddingVertical: 7,
    borderLeftWidth: 3,
    borderRadius: Radius.sm,
  },
  messageTime: {
    paddingHorizontal: 4,
    fontSize: 11,
    lineHeight: 15,
  },
  /**
   * `minHeight`, NOT `height`.
   *
   * A hard height would guarantee the invariant and break accessibility: the
   * caption scales up to `maxFontSizeMultiplier` 1.4 with the OS font setting,
   * and a 15pt box would clip a 21pt line. The invariant does not need it —
   * the marker is out of flow (see `messageTimeMarker`), so the ONLY in-flow
   * child is the timestamp, and the timestamp is rendered on every state. The
   * slot therefore measures the same whether the message is pending or sent,
   * at any font scale. The floor is only here so an empty formatter result
   * cannot collapse the row.
   */
  messageTimeSlot: {
    minHeight: 15,
    justifyContent: "center",
  },
  /** Present, measured, and not shown. */
  messageTimeReserved: {
    opacity: 0,
  },
  messageTimeMarker: {
    // Laid OVER the reserved timestamp rather than beside it, so raising the
    // marker cannot change the slot's width any more than its height.
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    fontSize: 11,
    textAlign: "center",
    textAlignVertical: "center",
  },
  messageMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  reactionPill: {
    minWidth: 30,
    minHeight: 24,
    paddingHorizontal: 8,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    borderWidth: 1,
  },
  reactionPillPressed: {
    opacity: 0.6,
  },
  reactionPillText: {
    fontSize: 14,
    lineHeight: 18,
  },
  reactionPicker: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 4,
    padding: 4,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  reactionButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
  },
  reactionButtonText: {
    fontSize: 18,
    lineHeight: 22,
  },
  hiddenReactionInput: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
  },
  composer: {
    width: "100%",
    maxWidth: CHAT_MAX_WIDTH,
    alignSelf: "center",
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.md,
    borderTopWidth: 1,
    gap: Spacing.sm,
  },
  composerWeb: {
    paddingTop: Spacing.sm,
  },
  replyComposer: {
    minHeight: 52,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  replyAccent: {
    width: 3,
    alignSelf: "stretch",
    borderRadius: 2,
  },
  replyComposerCopy: {
    flex: 1,
    minWidth: 0,
  },
  cancelReply: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 15,
  },
  inputBar: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: Spacing.sm,
  },
  inactiveBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderTopWidth: 1,
  },
  input: {
    flex: 1,
    maxHeight: 120,
    minHeight: 44,
    borderRadius: Radius.lg,
    borderWidth: 1.5,
    paddingHorizontal: Spacing.lg,
    paddingTop: Platform.OS === "ios" ? 12 : 8,
    paddingBottom: Platform.OS === "ios" ? 12 : 8,
    fontSize: 16,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  deckOpenBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  sendBtnDisabled: {
    opacity: 0.45,
  },
  modalScreen: {
    flex: 1,
    justifyContent: "flex-end",
  },
  modalBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(15, 23, 42, 0.42)",
  },
  endSheet: {
    margin: Spacing.lg,
    padding: Spacing.lg,
    gap: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    // Capped, not grown-to-fit: the sheet holds its height whichever rows are
    // present and the options scroll within it.
    maxHeight: "76%",
  },
  endSheetScrollContent: {
    gap: Spacing.md,
  },
  endSheetHeader: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: -Spacing.xs,
  },
  endClose: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
  },
  endOption: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  endOptionCopy: {
    flex: 1,
    gap: 2,
  },
  deckSheet: {
    width: "94%",
    maxWidth: 820,
    alignSelf: "center",
    marginBottom: Spacing.md,
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 18,
    gap: 14,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
    borderWidth: 1,
    borderColor: "rgba(112, 147, 184, 0.22)",
    overflow: "hidden",
    shadowColor: "#000000",
    shadowOpacity: 0.35,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: -10 },
  },
  deckHandle: {
    alignSelf: "center",
    width: 60,
    height: 6,
    borderRadius: 3,
    backgroundColor: "#90A3BA",
    opacity: 0.78,
    marginBottom: 8,
  },
  deckHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: Spacing.sm,
  },
  deckTitleCopy: {
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  deckTitle: {
    color: "#F7FBFF",
    fontSize: 28,
    lineHeight: 34,
    fontWeight: "900",
  },
  deckSubtitle: {
    color: "#8F9DB0",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "500",
  },
  deckCloseBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(43, 59, 80, 0.72)",
  },
  deckCategoryArea: {
    position: "relative",
    marginTop: 2,
  },
  deckCategoryScroll: {
    width: "100%",
    minWidth: 0,
  },
  deckCategoryList: {
    gap: 10,
    paddingRight: 2,
    paddingBottom: 4,
  },
  deckCategoryPeek: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 4,
    width: 20,
  },
  deckCategoryPill: {
    minHeight: 36,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    paddingHorizontal: 11,
    paddingVertical: 7,
    borderRadius: Radius.pill,
    borderWidth: 1.25,
  },
  deckCategoryPillSelected: {
    backgroundColor: "#3B95FF",
    borderColor: "#74B9FF",
    shadowColor: "#3B95FF",
    shadowOpacity: 0.45,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 4 },
  },
  deckCategoryPillIdle: {
    backgroundColor: "rgba(6, 17, 29, 0.38)",
    borderColor: "rgba(116, 144, 176, 0.24)",
  },
  deckCategoryText: {
    color: "#95A2B5",
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "700",
  },
  deckCategoryTextSelected: {
    color: "#FFFFFF",
  },
  deckPromptCard: {
    width: "100%",
    maxWidth: 820,
    alignSelf: "center",
    paddingHorizontal: 18,
    paddingVertical: 16,
    borderRadius: 16,
    borderWidth: 1,
    backgroundColor: "rgba(11, 30, 47, 0.72)",
    borderColor: "rgba(95, 128, 163, 0.25)",
    // Fixed, not content-sized: sized for a three-line prompt plus the
    // follow-up row, so drawing a longer or shorter prompt never resizes the
    // sheet under the user's thumb. Rare taller prompts scroll inside.
    height: 224,
  },
  deckPromptCardContent: {
    gap: 11,
  },
  deckPromptLoading: {
    // Centred in the card's fixed height (224 minus padding and badge row),
    // so the spinner sits where the prompt is about to appear.
    minHeight: 140,
    alignItems: "center",
    justifyContent: "center",
  },
  deckPromptBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  deckPromptBadgeText: {
    color: "#7DAFFF",
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "800",
    letterSpacing: 1.4,
    textTransform: "uppercase",
  },
  deckPromptText: {
    color: "#F8FBFF",
    fontSize: 21,
    lineHeight: 27,
    // Overrides the title variant's 800. The prompt is a question to read
    // aloud, not a heading — at 800 it shouted over the whole sheet.
    fontWeight: "600",
  },
  deckPromptHint: {
    color: "#8D9CAF",
    fontSize: 12,
    lineHeight: 18,
    fontWeight: "500",
  },
  deckFollowUp: {
    // Web's fixed-height card leaves the follow-up crowding the divider;
    // native keeps the tighter spacing because its card has less room.
    marginTop: Platform.OS === "web" ? 14 : 2,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(119, 148, 178, 0.22)",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  deckFollowUpIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(84, 113, 143, 0.36)",
  },
  deckFollowUpCopy: {
    flex: 1,
    minWidth: 0,
    gap: 5,
  },
  deckFollowUpHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  deckFollowUpLabel: {
    flex: 1,
    minWidth: 0,
    color: "#81B3FF",
    fontSize: 10,
    lineHeight: 14,
    fontWeight: "800",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  deckFollowUpText: {
    color: "#F4F8FF",
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "600",
  },
  deckDraftCard: {
    width: "100%",
    maxWidth: 820,
    alignSelf: "center",
    gap: Spacing.sm,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  deckDraftTitle: {
    color: "#F7FBFF",
    fontWeight: "700",
  },
  deckDraftBody: {
    color: "#A8B6C8",
    fontWeight: "500",
  },
  deckActions: {
    width: "100%",
    maxWidth: 820,
    alignSelf: "center",
    flexDirection: Platform.OS === "web" ? "row" : "column",
    justifyContent: "center",
    alignItems: Platform.OS === "web" ? "center" : "stretch",
    gap: 10,
    marginTop: 2,
  },
  deckActionBtn: {
    flex: Platform.OS === "web" ? 1 : undefined,
    minHeight: 40,
    width: Platform.OS === "web" ? undefined : "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: Radius.pill,
    borderWidth: 1.5,
  },
  deckActionPrimary: {
    borderWidth: 0,
  },
  deckAddBtn: {
    backgroundColor: "#4E9DFF",
    borderColor: "#4E9DFF",
  },
  deckActionLabel: {
    flexShrink: 1,
    minWidth: 0,
    textAlign: "center",
  },
  deckAddBtnText: {
    color: "#06111D",
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "800",
  },
  deckSecondaryBtn: {
    backgroundColor: "rgba(6, 17, 29, 0.22)",
    borderColor: "#4E9DFF",
  },
  deckSecondaryText: {
    color: "#4E9DFF",
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "800",
  },
  deckGhostBtn: {
    flexGrow: 0,
    backgroundColor: "transparent",
    borderColor: "rgba(132, 154, 180, 0.35)",
  },
  deckGhostText: {
    color: "#A7B7CA",
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "700",
  },
  deckPhraseMyself: {
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: -2,
    paddingHorizontal: 10,
    paddingVertical: 2,
  },
  deckPhraseMyselfText: {
    color: "#8394AB",
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "700",
  },
});
